<template>
	<div>
		<el-breadcrumb separator-icon="ArrowRight" style="margin: 16px">
			<el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
			<el-breadcrumb-item>方案管理</el-breadcrumb-item>
			<el-breadcrumb-item>方案生成</el-breadcrumb-item>
		</el-breadcrumb>

		<el-card style="margin: 15px; min-height: calc(100vh - 111px)">
			<div class="scheme-generator">
				<!-- 行程配置区域 -->
				<div class="config-section">
					<h2 class="section-title">行程配置</h2>
					<el-form :model="form" label-width="120px">
						<el-form-item label="行程开始日期">
							<el-date-picker v-model="form.startDate" type="date" placeholder="请选择行程开始日期"
								value-format="YYYY-MM-DD" :disabled-date="disabledDate" style="width: 200px;"
								@change="handleStartDateChange">
								<template #prefix>
									<i class="el-icon-date"></i>
								</template>
							</el-date-picker>
							<div style="color: #909399; font-size: 12px; margin-top: 5px;">
								行程将从 {{ form.startDate || '未选择日期' }} 开始
							</div>
						</el-form-item>

						<el-form-item label="行程天数">
							<div class="custom-days-control">
								<el-button :disabled="form.days <= 1" @click="decreaseDays" class="control-btn">
									-
								</el-button>
								<el-input v-model="form.days" type="number" :min="1" :max="30" @blur="validateDays"
									class="days-input" />
								<el-button :disabled="form.days >= 30" @click="increaseDays" class="control-btn">
									+
								</el-button>
							</div>
						</el-form-item>

						<!-- 每日行程选择区域 -->
						<div class="daily-locations" v-if="form.days > 0">
							<h3 class="sub-title">每日行程安排</h3>
							<div v-for="day in form.days" :key="day" class="day-section">
								<div class="day-header">
									<h4 class="day-title">第 {{ day }} 天 ({{ getDayDate(day) }})</h4>
								</div>

								<div class="day-config">
									<!-- 地点选择 -->
									<div class="config-item">
										<label class="config-label">行程地点：</label>
										<el-select v-model="form.dailyLocations[day-1]" :placeholder="`选择第${day}天行程地点`"
											class="location-select" filterable :filter-method="filterLocations"
											:loading="loadingLocations" @focus="loadLocations" clearable
											style="width: 300px;">
											<template
												v-if="filteredLocationGroups && filteredLocationGroups.length > 0">
												<el-option-group v-for="group in filteredLocationGroups"
													:key="group.label" :label="group.label">
													<el-option v-for="item in group.options" :key="item.id"
														:label="item.name" :value="item.id"
														:disabled="isLocationSelected(item.id, day-1)">
														<div class="location-option">
															<div class="location-name">{{ item.name }}</div>
															<div v-if="item.address" class="location-address">
																{{ item.address }}
															</div>
														</div>
													</el-option>
												</el-option-group>
											</template>

											<template v-else>
												<el-option value="" disabled>暂无地点数据</el-option>
											</template>

											<template #prefix>
												<i class="el-icon-location"></i>
											</template>
										</el-select>

										<!-- 已选地点预览 -->
										<div v-if="getSelectedLocation(day-1)"
											class="selected-preview location-preview">
											<el-image v-if="getSelectedLocation(day-1).picture"
												:src="getSelectedLocation(day-1).picture" fit="cover"
												class="preview-image"
												:preview-src-list="[getSelectedLocation(day-1).picture]">
												<template #error>
													<div class="image-error">
														<i class="el-icon-picture-outline"></i>
														<span>暂无图片</span>
													</div>
												</template>
											</el-image>
											<div v-else class="preview-placeholder">
												<i class="el-icon-picture-outline"></i>
											</div>
											<div class="preview-info">
												<div class="preview-name">{{ getSelectedLocation(day-1).name }}</div>
											</div>
										</div>
									</div>

									<!-- 酒店选择 - 最后一天不显示 -->
									<div class="config-item" v-if="day < form.days">
										<label class="config-label">住宿酒店：</label>
										<div style="display: flex; align-items: center; gap: 10px;">
											<el-select v-model="form.dailyHotels[day-1]" :placeholder="`选择第${day}天住宿酒店`"
												class="hotel-select" filterable :filter-method="filterHotels"
												:loading="loadingHotels" @focus="loadHotels" clearable
												style="width: 300px;" :disabled="day === form.days"
												@change="handleHotelChange(day-1)">
												<template v-if="filteredHotels && filteredHotels.length > 0">
													<el-option v-for="hotel in filteredHotels" :key="hotel.name"
														:label="hotel.name" :value="hotel.name">
														<div class="hotel-option">
															<div class="hotel-name">
																{{ hotel.name }}
															</div>
															<div class="hotel-details">
																<el-tag v-if="hotel.price" size="small" type="success">
																	¥{{ hotel.price }}/晚
																</el-tag>
																<el-tag v-if="hotel.obtainingTime" size="small"
																	type="info">
																	<i class="el-icon-date"></i>
																	{{ formatHotelDate(hotel.obtainingTime) }}
																</el-tag>
															</div>
															<div v-if="hotel.description" class="hotel-description">
																{{ formatDescriptionPreview(hotel.description) }}
															</div>
															<div v-if="!isHotelAvailable(day-1, hotel.name)"
																class="hotel-disabled">
																<i class="el-icon-warning"></i> 已选择
															</div>
														</div>
													</el-option>
												</template>
												<template v-else>
													<el-option value="" disabled>暂无酒店数据</el-option>
												</template>
												<template #prefix>
													<i class="el-icon-office-building"></i>
												</template>
											</el-select>

											<div v-if="form.dailyHotels[day-1]" class="room-count-container">
												<!-- 替换原来的 el-input-number -->
												<div class="custom-rooms-control">
													<el-button :disabled="getRoomCount(day-1) <= 1"
														@click="decreaseRoom(day-1)" class="control-btn" size="small">
														-
													</el-button>
													<el-input v-model="form.dailyHotelRooms[day-1]" type="number"
														:min="1" :max="20" @blur="validateRoom(day-1)"
														@input="validateRoom(day-1)" class="rooms-input" size="small">
														<!-- <template #prepend>
															<span class="input-prepend-text">房间</span>
														</template> -->
													</el-input>
													<el-button :disabled="getRoomCount(day-1) >= 20"
														@click="increaseRoom(day-1)" class="control-btn" size="small">
														+
													</el-button>
												</div>
											</div>
										</div>

										<!-- 已选酒店预览 -->
										<div v-if="getSelectedHotel(day-1)" class="selected-preview hotel-preview">
											<div class="preview-info">
												<div class="preview-header">
													<div class="preview-name">
														<i class="el-icon-office-building"></i>
														{{ getSelectedHotel(day-1).name }}
													</div>
													<div v-if="getSelectedHotel(day-1).price" class="preview-price">
														¥{{ getHotelPriceForDay(day-1) }}/晚/间 ×
														{{ form.dailyHotelRooms[day-1] || 1 }}间
													</div>
												</div>
												<div v-if="getSelectedHotel(day-1).address" class="preview-address">
													<i class="el-icon-location"></i>
													{{ getSelectedHotel(day-1).address }}
												</div>
												<div v-if="getSelectedHotel(day-1).description" class="preview-desc">
													{{ formatDescriptionPreview(getSelectedHotel(day-1).description) }}
												</div>
											</div>
										</div>
									</div>

									<!-- 每日餐饮安排 -->
									<div class="config-item meal-config">
										<label class="config-label">餐饮安排：</label>
										<div class="meal-arrangement daily-meal">
											<el-checkbox-group v-model="form.dailyMeals[day-1]"
												class="meal-checkbox-group">
												<el-checkbox label="breakfast" :value="true">早餐</el-checkbox>
												<el-checkbox label="lunch" :value="true">午餐</el-checkbox>
												<el-checkbox label="dinner" :value="true">晚餐</el-checkbox>
											</el-checkbox-group>
											<div class="meal-price-info" v-if="calculateDailyMealCost(day-1) > 0">
												餐饮费用: ¥{{ calculateDailyMealCost(day-1) }}/人
											</div>
											<div v-else class="meal-price-info no-meal">
												餐饮: 不含
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<el-form-item label="选择景点">
							<el-transfer v-model="form.selectedAttractions" :data="allAttractions"
								:titles="['所有景点', '已选景点']" filterable :filter-method="filterAttractions"
								filter-placeholder="输入景点名称搜索" :loading="loadingAttractions">
								<template #left-footer>
									<div class="transfer-footer">
										<el-button type="text" @click="selectAllAttractions">全选</el-button>
										<el-button type="text" @click="clearAllAttractions">清空</el-button>
										<span class="count-text">共 {{ allAttractions.length }} 个景点</span>
									</div>
								</template>
								<template #right-footer>
									<div class="transfer-footer">
										<span class="count-text">已选 {{ form.selectedAttractions.length }} 个景点</span>
									</div>
								</template>
							</el-transfer>
						</el-form-item>

						<!-- 导游费用 -->
						<el-form-item label="导游费用">
							<div style="display: flex; align-items: center; gap: 20px;">
								<el-input-number v-model="form.guideFeePerDay" :min="0" :max="10000" :precision="2"
									label="导游费用/天" style="width: 200px;">
									<template #prefix>¥</template>
								</el-input-number>
								<div style="display: flex; align-items: center; gap: 8px;">
									<span style="min-width: 60px; white-space: nowrap;">天数：</span>
									<el-input-number v-model="form.guideDays" :min="1" :max="form.days" :precision="0"
										label="导游天数" style="width: 120px;">
										<template #suffix>天</template>
									</el-input-number>
								</div>
							</div>
							<div style="color: #909399; font-size: 12px; margin-top: 5px;">
								导游费用小计: ¥{{ calculateGuideTotal() }}
							</div>
						</el-form-item>

						<!-- 车型选择 -->
						<el-form-item label="车辆租赁">
							<div style="display: flex; align-items: center; gap: 20px;">
								<el-select v-model="form.carType" placeholder="请选择车型" class="car-select" filterable
									:filter-method="filterCarTypes" :loading="loadingCarTypes" @focus="loadCarTypes"
									style="width: 300px;">
									<el-option v-for="item in filteredCarTypes" :key="item.id" :label="item.name"
										:value="item.id">
										<div class="car-option">
											<div class="car-name">{{ item.name }}</div>
											<div class="car-details">
												<span>价格: ¥{{ item.pricePerDay }}/天</span>
											</div>
										</div>
									</el-option>

									<template #empty>
										<div class="no-data" v-if="!loadingCarTypes">
											<i class="el-icon-box"></i>
											<span>暂无车型数据</span>
										</div>
									</template>
								</el-select>

								<div style="display: flex; align-items: center; gap: 8px;">
									<span style="min-width: 60px; white-space: nowrap;">天数：</span>
									<el-input-number v-model="form.carDays" :min="1" :max="form.days" :precision="0"
										label="车辆租赁天数" style="width: 120px;">
										<template #suffix>天</template>
									</el-input-number>
								</div>
							</div>
							<div style="color: #909399; font-size: 12px; margin-top: 5px;">
								车辆租赁费用小计: ¥{{ calculateCarTotal() }}
							</div>
						</el-form-item>

					</el-form>

					<div class="action-buttons">
						<el-button type="primary" icon="MagicStick" @click="generateScheme"
							:disabled="!canGenerateScheme" :loading="generatingScheme">
							生成行程方案
						</el-button>
						<el-button type="success" icon="Document" @click="exportItineraryToWord"
							:disabled="!schemeGenerated">
							导出Word文档
						</el-button>
						<el-button type="info" icon="Refresh" @click="resetForm">
							重置表单
						</el-button>
					</div>
				</div>

				<!-- 行程预览区域 -->
				<div class="preview-section" v-if="schemeGenerated">
					<h2 class="section-title">行程预览</h2>

					<!-- 日期范围显示 -->
					<div class="date-range-info">
						<el-tag type="info" size="large">
							<i class="el-icon-date"></i>
							行程日期: {{ tripDateRange }}
						</el-tag>
					</div>

					<!-- 概览卡片区域 -->
					<div class="overview-cards">
						<el-card shadow="hover" class="overview-card">
							<div class="card-content">
								<i class="el-icon-date"></i>
								<div>
									<div class="card-title">行程天数</div>
									<div class="card-value">{{ form.days }} 天</div>
								</div>
							</div>
						</el-card>

						<el-card shadow="hover" class="overview-card">
							<div class="card-content">
								<i class="el-icon-view"></i>
								<div>
									<div class="card-title">景点数量</div>
									<div class="card-value">{{ totalAttractions }} 个</div>
								</div>
							</div>
						</el-card>

						<el-card shadow="hover" class="overview-card">
							<div class="card-content">
								<i class="el-icon-ticket"></i>
								<div>
									<div class="card-title">门票总费用</div>
									<div class="card-value">¥ {{ totalTicketFee }}</div>
								</div>
							</div>
						</el-card>

						<el-card shadow="hover" class="overview-card">
							<div class="card-content">
								<i class="el-icon-money"></i>
								<div>
									<div class="card-title">总费用估算</div>
									<div class="card-value">¥ {{ totalEstimatedCost }}</div>
								</div>
							</div>
						</el-card>
					</div>

					<!-- 行程预览标签页 -->
					<el-tabs v-model="activeTab" type="border-card" class="preview-tabs">
						<el-tab-pane label="行程详情" name="details">
							<div class="itinerary-details">
								<template v-if="generatedScheme && generatedScheme.days">
									<div v-for="(day, index) in generatedScheme.days" :key="index"
										class="day-itinerary">
										<div class="day-header">
											<div class="day-number">Day {{ String(index + 1).padStart(2, '0') }}</div>
											<div class="day-date">{{ day.date }}</div>
										</div>

										<div class="itinerary-title">
											{{ day.locationInfo && day.locationInfo.name ? day.locationInfo.name : '暂无地点信息' }}
										</div>
										<div class="location-description">
											<div v-if="day.locationInfo && day.locationInfo.description">
												<div v-for="(line, lineIndex) in formatDescription(day.locationInfo.description)"
													:key="lineIndex" class="description-line">
													{{ line }}
												</div>
											</div>
											<div v-else class="no-description">
												暂无行程描述
											</div>
										</div>
									</div>
								</template>
								<div v-else class="no-itinerary">
									暂无行程详情
								</div>
							</div>
						</el-tab-pane>

						<el-tab-pane label="费用明细" name="cost">
							<div class="cost-breakdown">
								<h3 class="sub-title">费用总览</h3>
								<el-table :data="costDetails" style="width: 100%">
									<el-table-column prop="item" label="费用项目" width="200"></el-table-column>
									<el-table-column prop="unitPrice" label="单价" width="120"></el-table-column>
									<el-table-column prop="quantity" label="数量" width="100"></el-table-column>
									<el-table-column prop="total" label="小计"></el-table-column>
								</el-table>

								<!-- 酒店明细 -->
								<h3 class="sub-title" style="margin-top: 20px;">酒店明细</h3>
								<el-table :data="hotelCostDetails" style="width: 100%">
									<el-table-column prop="day" label="天数" width="80"></el-table-column>
									<el-table-column prop="hotelName" label="酒店名称" min-width="200">
										<template #default="scope">
											<div class="hotel-name-cell">
												<div class="hotel-name">{{ scope.row.hotelName }}</div>
												<div class="hotel-address"
													v-if="scope.row.address && scope.row.address !== '未知地址'">
													<i class="el-icon-location"></i> {{ scope.row.address }}
												</div>
											</div>
										</template>
									</el-table-column>
									<el-table-column prop="price" label="单价(元/间)" width="120">
										<template #default="scope">
											¥{{ scope.row.price }}
										</template>
									</el-table-column>
									<el-table-column prop="quantity" label="房间数" width="80">
										<template #default="scope">
											{{ scope.row.quantity || 1 }}间
										</template>
									</el-table-column>
									<el-table-column prop="total" label="小计(元)" width="120">
										<template #default="scope">
											¥{{ scope.row.total }}
										</template>
									</el-table-column>
								</el-table>

								<div class="cost-summary">
									<div class="subtotal">酒店费用小计: ¥{{ totalHotelCost }}</div>
									<div class="summary-note">以上为住宿费用，按天/晚计算</div>
								</div>

								<!-- 景点明细 -->
								<h3 class="sub-title" style="margin-top: 20px;">景点明细</h3>
								<el-table :data="attractionCostDetails" style="width: 100%">
									<el-table-column prop="day" label="天数" width="80"></el-table-column>
									<el-table-column prop="attractions" label="景点名称及费用" min-width="300">
										<template #default="scope">
											<div class="day-attractions">
												<div v-if="scope.row.attractions && scope.row.attractions.length > 0">
													<div v-for="(attraction, index) in scope.row.attractions"
														:key="index" class="attraction-item">
														<div class="attraction-name">{{ attraction.name }}</div>
														<div class="attraction-price">¥{{ attraction.price }}</div>
													</div>
												</div>
												<div v-else class="no-attractions">无景点安排</div>
											</div>
										</template>
									</el-table-column>
									<el-table-column prop="total" label="小计(元)" width="120">
										<template #default="scope">
											<span class="day-attraction-total">¥{{ scope.row.total }}</span>
										</template>
									</el-table-column>
								</el-table>

								<div class="cost-summary">
									<div class="subtotal">景点门票小计: ¥{{ totalTicketFee }}</div>
									<div class="summary-note">以上为景点门票费用</div>
								</div>
								<h3 class="sub-title" style="margin-top: 20px;">餐饮明细</h3>
								<el-table :data="mealCostDetails" style="width: 100%">
									<el-table-column prop="day" label="天数" width="80"></el-table-column>
									<el-table-column prop="breakfast" label="早餐" width="100">
										<template #default="scope">
											<span v-if="scope.row.breakfast.included">包含
												¥{{ scope.row.breakfast.price }}/人</span>
											<span v-else class="meal-excluded">不含</span>
										</template>
									</el-table-column>
									<el-table-column prop="lunch" label="午餐" width="100">
										<template #default="scope">
											<span v-if="scope.row.lunch.included">包含
												¥{{ scope.row.lunch.price }}/人</span>
											<span v-else class="meal-excluded">不含</span>
										</template>
									</el-table-column>
									<el-table-column prop="dinner" label="晚餐" width="100">
										<template #default="scope">
											<span v-if="scope.row.dinner.included">包含
												¥{{ scope.row.dinner.price }}/人</span>
											<span v-else class="meal-excluded">不含</span>
										</template>
									</el-table-column>
									<el-table-column prop="total" label="小计(元)">
										<template #default="scope">
											<span class="day-meal-total">¥{{ scope.row.total }}</span>
										</template>
									</el-table-column>
								</el-table>

								<div class="cost-summary">
									<div class="subtotal">餐饮费用小计: ¥{{ totalMealCost }}</div>
									<div class="summary-note">以上为餐饮费用</div>
								</div>

								<div class="cost-summary">
									<div class="total-cost">总费用估算: ¥{{ totalEstimatedCost }}</div>
									<div class="cost-note">注: 以上为估算费用，具体以实际消费为准</div>
								</div>
							</div>

						</el-tab-pane>
					</el-tabs>
				</div>
			</div>
		</el-card>
	</div>
</template>

<script>
	import request from "@/utils/request";
	import {
		ElMessage,
		ElLoading
	} from "element-plus";
	import dayjs from "dayjs";

	export default {
		name: "SchemeGenerator",
		data() {
			return {
				form: {
					startDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
					days: 1,
					dailyLocations: [],
					dailyHotels: [],
					dailyHotelRooms: [],
					dailyMeals: [
						['breakfast', 'lunch', 'dinner']
					],
					guideDays: 1,
					carDays: 1,
					attractionsPerDay: 1,
					selectedAttractions: [],
					hotelRecommendationCount: 1,
					guideFeePerDay: 500,
					carType: null
				},
				mealPrices: {
					breakfast: 30,
					lunch: 60,
					dinner: 60
				},
				allHotels: [],
				uniqueHotels: [],
				locationList: [],
				loadingLocations: false,
				attractionList: [],
				loadingAttractions: false,
				allCarTypes: [],
				carTypes: [],
				loadingCarTypes: false,
				hotelList: [],
				loadingHotels: false,
				generatedScheme: null,
				schemeGenerated: false,
				totalAttractions: 0,
				totalTicketFee: 0,
				totalEstimatedCost: 0,
				totalMealCost: 0,
				totalHotelCost: 0,
				activeTab: "details",
				costDetails: [],
				mealCostDetails: [],
				hotelCostDetails: [],
				attractionCostDetails: [],
				searchQuery: "",
				locationGroups: [],
				generatingScheme: false,
				carTypeSearchQuery: "",
				attractionSearchQuery: "",
				hotelSearchQuery: "",
				selectedHotels: [],
				selectedAttractionsInfo: [],

				// 酒店时间段映射
				hotelTimeSegments: new Map(),
				// 车型时间段映射
				carTimeSegments: new Map()
			};
		},
		computed: {
			tripDateRange() {
				if (!this.form.startDate || this.form.days <= 0) return "";
				const start = this.form.startDate;
				const end = dayjs(this.form.startDate).add(this.form.days - 1, 'day').format('YYYY-MM-DD');
				if (this.form.days === 1) {
					return start;
				}
				return `${start} 至 ${end}`;
			},
			dailyDates() {
				const dates = [];
				for (let i = 0; i < this.form.days; i++) {
					dates.push({
						day: i + 1,
						date: dayjs(this.form.startDate).add(i, 'day').format('YYYY-MM-DD')
					});
				}
				return dates;
			},
			allAttractions() {
				return this.attractionList.map(item => ({
					key: item.id,
					label: item.name,
					image: item.picture
				}));
			},
			filteredLocationGroups() {
				if (!this.searchQuery) {
					return this.locationGroups;
				}
				const query = this.searchQuery.toLowerCase().trim();
				return this.locationGroups.filter(group =>
					group.label && group.label.toLowerCase().includes(query)
				);
			},
			filteredCarTypes() {
				if (!this.carTypeSearchQuery) {
					return this.carTypes;
				}
				const query = this.carTypeSearchQuery.toLowerCase();
				return this.carTypes.filter(item =>
					item.name.toLowerCase().includes(query) ||
					(item.description && item.description.toLowerCase().includes(query))
				);
			},
			filteredHotels() {
				if (!this.hotelSearchQuery) {
					return this.uniqueHotels;
				}
				const query = this.hotelSearchQuery.toLowerCase().trim();
				return this.uniqueHotels.filter(hotel =>
					hotel.name && hotel.name.toLowerCase().includes(query) ||
					(hotel.address && hotel.address.toLowerCase().includes(query)) ||
					(hotel.description && hotel.description.toLowerCase().includes(query))
				);
			},
			canGenerateScheme() {
				if (!this.form.startDate) {
					return false;
				}

				if (this.form.days <= 0 || this.form.days > 30) {
					return false;
				}

				if (!this.form.guideDays || this.form.guideDays < 1) {
					return false;
				}

				if (this.form.guideDays > this.form.days) {
					return false;
				}

				if (!this.form.carDays || this.form.carDays < 1) {
					return false;
				}

				if (this.form.carDays > this.form.days) {
					return false;
				}

				const hasAllLocations = this.form.dailyLocations.slice(0, this.form.days)
					.every(location => !!location);

				let hasAllHotels = true;
				const hotelDays = Math.max(0, this.form.days - 1);

				if (hotelDays > 0) {
					hasAllHotels = this.form.dailyHotels.slice(0, hotelDays)
						.every(hotel => hotel && hotel.trim() !== "");
				}

				const hasAttractions = this.form.selectedAttractions.length > 0;
				const hasCarType = !!this.form.carType;

				return hasAllLocations && hasAllHotels && hasAttractions && hasCarType;
			},
			selectedCarType() {
				if (!this.form.carType) return {
					name: "未选择车型",
					pricePerDay: 0
				};
				const car = this.carTypes.find(item => item.id === this.form.carType);
				return car || {
					name: "未知车型",
					pricePerDay: 0
				};
			},
			selectedAttractionsInfo() {
				return this.form.selectedAttractions.map(id => {
					const attraction = this.attractionList.find(item => item.id === id);
					return attraction ? {
						id: attraction.id,
						name: attraction.name,
						price: attraction.ticketPrice || 0,
						description: attraction.description || '',
						location: attraction.location
					} : null;
				}).filter(item => item !== null);
			}
		},
		methods: {
			// 解析日期区间字符串
			parseTimeRange(timeRangeStr) {
				if (!timeRangeStr || timeRangeStr.trim() === '') {
					console.log("日期区间字符串为空");
					return null;
				}

				try {
					const parts = timeRangeStr.split(',');
					if (parts.length !== 2) {
						console.log(`日期区间格式不正确: ${timeRangeStr}，期望格式: 开始日期,结束日期`);
						return null;
					}

					const startDate = dayjs(parts[0].trim());
					const endDate = dayjs(parts[1].trim());

					if (!startDate.isValid() || !endDate.isValid()) {
						console.log(`日期格式不正确: ${timeRangeStr}`);
						return null;
					}

					if (startDate.isAfter(endDate)) {
						console.log(`开始日期晚于结束日期: ${timeRangeStr}`);
						return null;
					}

					console.log(`解析日期区间成功: ${parts[0].trim()} 至 ${parts[1].trim()}`);
					return {
						start: startDate,
						end: endDate,
						startStr: parts[0].trim(),
						endStr: parts[1].trim(),
						original: timeRangeStr
					};
				} catch (error) {
					console.error(`解析日期区间失败: ${timeRangeStr}`, error);
					return null;
				}
			},

			// 判断日期是否在时间段内
			isDateInTimeRange(date, timeRange) {
				if (!date || !timeRange) {
					console.log("判断日期是否在时间段内：日期或时间段为空");
					return false;
				}

				const targetDate = dayjs(date);
				if (!targetDate.isValid()) {
					console.log(`目标日期无效: ${date}`);
					return false;
				}

				const inRange = targetDate.isSameOrAfter(timeRange.start) &&
					targetDate.isSameOrBefore(timeRange.end);

				console.log(`判断日期 ${date} 是否在时间段 ${timeRange.startStr} 至 ${timeRange.endStr} 内: ${inRange}`);
				return inRange;
			},

			// 计算日期与时间段的距离
			calculateTimeRangeDistance(date, timeRange) {
				if (!date || !timeRange) {
					console.log("计算日期与时间段距离：日期或时间段为空");
					return Infinity;
				}

				const targetDate = dayjs(date);
				if (!targetDate.isValid()) {
					console.log(`目标日期无效: ${date}`);
					return Infinity;
				}

				let distance = Infinity;

				if (targetDate.isBefore(timeRange.start)) {
					distance = Math.abs(targetDate.diff(timeRange.start, 'day'));
				} else if (targetDate.isAfter(timeRange.end)) {
					distance = Math.abs(targetDate.diff(timeRange.end, 'day'));
				} else {
					distance = 0; // 在时间段内
				}

				console.log(`日期 ${date} 与时间段 ${timeRange.startStr} 至 ${timeRange.endStr} 的距离: ${distance} 天`);
				return distance;
			},

			// 处理酒店时间段
			processHotelTimeSegments() {
				this.hotelTimeSegments.clear();
				console.log("开始处理酒店时间段...");

				this.allHotels.forEach((hotel, index) => {
					if (!hotel.name || hotel.name.trim() === '') {
						console.log(`第 ${index + 1} 条酒店记录名称为空，跳过`);
						return;
					}

					const hotelName = hotel.name.trim();
					const price = parseFloat(hotel.price || 0);
					const timeRangeStr = hotel.obtainingTime || "";

					console.log(`处理酒店 ${hotelName}，价格: ${price}，时间段: ${timeRangeStr}`);

					if (!this.hotelTimeSegments.has(hotelName)) {
						this.hotelTimeSegments.set(hotelName, []);
					}

					if (timeRangeStr) {
						const timeRange = this.parseTimeRange(timeRangeStr);
						if (timeRange && price > 0) {
							console.log(
								`添加酒店时间段: ${hotelName}，${timeRange.startStr} 至 ${timeRange.endStr}，价格: ${price}`
								);
							this.hotelTimeSegments.get(hotelName).push({
								...timeRange,
								price: price
							});
						} else if (!timeRange) {
							console.log(`酒店 ${hotelName} 的时间段解析失败: ${timeRangeStr}`);
						}
					} else {
						console.log(`酒店 ${hotelName} 没有时间段信息，跳过`);
					}
				});

				// 对每个酒店的时间段按开始日期排序
				this.hotelTimeSegments.forEach((segments, hotelName) => {
					if (segments.length > 1) {
						segments.sort((a, b) => a.start.diff(b.start));
						console.log(`酒店 ${hotelName} 有 ${segments.length} 个时间段，已排序`);
					}
				});

				console.log("酒店时间段处理完成");
				console.log("酒店时间段映射:", this.hotelTimeSegments);
			},

			// 处理车型时间段
			processCarTimeSegments() {
				this.carTimeSegments.clear();
				console.log("开始处理车型时间段...");

				this.allCarTypes.forEach((car, index) => {
					if (!car.name || car.name.trim() === '') {
						console.log(`第 ${index + 1} 条车型记录名称为空，跳过`);
						return;
					}

					const carName = car.name.trim();
					const price = parseFloat(car.pricePerDay || 0);
					const timeRangeStr = car.obtainingTime || "";

					console.log(`处理车型 ${carName}，价格: ${price}，时间段: ${timeRangeStr}`);

					if (!this.carTimeSegments.has(carName)) {
						this.carTimeSegments.set(carName, []);
					}

					if (timeRangeStr) {
						const timeRange = this.parseTimeRange(timeRangeStr);
						if (timeRange && price > 0) {
							console.log(
								`添加车型时间段: ${carName}，${timeRange.startStr} 至 ${timeRange.endStr}，价格: ${price}`);
							this.carTimeSegments.get(carName).push({
								...timeRange,
								price: price
							});
						} else if (!timeRange) {
							console.log(`车型 ${carName} 的时间段解析失败: ${timeRangeStr}`);
						}
					} else {
						console.log(`车型 ${carName} 没有时间段信息，跳过`);
					}
				});

				// 对每个车型的时间段按开始日期排序
				this.carTimeSegments.forEach((segments, carName) => {
					if (segments.length > 1) {
						segments.sort((a, b) => a.start.diff(b.start));
						console.log(`车型 ${carName} 有 ${segments.length} 个时间段，已排序`);
					}
				});

				console.log("车型时间段处理完成");
				console.log("车型时间段映射:", this.carTimeSegments);
			},

			// 根据日期获取酒店价格
			getHotelPriceByDate(hotelName, targetDateStr) {
				if (!hotelName || !targetDateStr) {
					console.log("获取酒店价格失败：酒店名或日期为空", {
						hotelName,
						targetDateStr
					});
					return 0;
				}

				const targetDate = dayjs(targetDateStr);
				if (!targetDate.isValid()) {
					console.log("获取酒店价格失败：目标日期无效", targetDateStr);
					return 0;
				}

				const trimmedHotelName = hotelName.trim();
				console.log(`查询酒店价格: ${trimmedHotelName}，目标日期: ${targetDateStr}`);

				// 1. 查找酒店的时间段
				const timeSegments = this.hotelTimeSegments.get(trimmedHotelName);
				if (!timeSegments || timeSegments.length === 0) {
					console.log(`酒店 ${trimmedHotelName} 没有时间段信息，使用默认价格`);
					return this.getHotelDefaultPrice(trimmedHotelName);
				}

				console.log(`酒店 ${trimmedHotelName} 有 ${timeSegments.length} 个时间段`);

				// 2. 查找完全匹配的时间段
				for (const segment of timeSegments) {
					if (this.isDateInTimeRange(targetDate, segment)) {
						console.log(`找到匹配的时间段: ${segment.startStr} 至 ${segment.endStr}，价格: ${segment.price}`);
						return segment.price;
					}
				}

				console.log(`没有找到完全匹配的时间段，开始查找最近的时间段`);

				// 3. 如果没有完全匹配，查找最近的时间段
				let closestSegment = null;
				let minDistance = Infinity;

				for (const segment of timeSegments) {
					const distance = this.calculateTimeRangeDistance(targetDate, segment);
					if (distance < minDistance) {
						minDistance = distance;
						closestSegment = segment;
					}
				}

				if (closestSegment && minDistance <= 30) { // 30天内认为是有效匹配
					console.log(
						`找到最近的时间段: ${closestSegment.startStr} 至 ${closestSegment.endStr}，距离: ${minDistance}天，价格: ${closestSegment.price}`
						);
					return closestSegment.price;
				} else if (closestSegment) {
					console.log(`找到时间段但距离太远(${minDistance}天)，不使用此时间段`);
				}

				// 4. 如果找不到合适的时间段，使用默认价格
				console.log(`没有找到合适的时间段，使用默认价格`);
				return this.getHotelDefaultPrice(trimmedHotelName);
			},

			// 根据日期获取车型价格
			getCarPriceByDate(carName, targetDateStr) {
				if (!carName || !targetDateStr) {
					console.log("获取车型价格失败：车型名或日期为空", {
						carName,
						targetDateStr
					});
					return 0;
				}

				const targetDate = dayjs(targetDateStr);
				if (!targetDate.isValid()) {
					console.log("获取车型价格失败：目标日期无效", targetDateStr);
					return 0;
				}

				const trimmedCarName = carName.trim();
				console.log(`查询车型价格: ${trimmedCarName}，目标日期: ${targetDateStr}`);

				// 1. 查找车型的时间段
				const timeSegments = this.carTimeSegments.get(trimmedCarName);
				if (!timeSegments || timeSegments.length === 0) {
					console.log(`车型 ${trimmedCarName} 没有时间段信息，使用默认价格`);
					return this.getCarDefaultPrice(trimmedCarName);
				}

				console.log(`车型 ${trimmedCarName} 有 ${timeSegments.length} 个时间段`);

				// 2. 查找完全匹配的时间段
				for (const segment of timeSegments) {
					if (this.isDateInTimeRange(targetDate, segment)) {
						console.log(`找到匹配的时间段: ${segment.startStr} 至 ${segment.endStr}，价格: ${segment.price}`);
						return segment.price;
					}
				}

				console.log(`没有找到完全匹配的时间段，开始查找最近的时间段`);

				// 3. 如果没有完全匹配，查找最近的时间段
				let closestSegment = null;
				let minDistance = Infinity;

				for (const segment of timeSegments) {
					const distance = this.calculateTimeRangeDistance(targetDate, segment);
					if (distance < minDistance) {
						minDistance = distance;
						closestSegment = segment;
					}
				}

				if (closestSegment && minDistance <= 30) { // 30天内认为是有效匹配
					console.log(
						`找到最近的时间段: ${closestSegment.startStr} 至 ${closestSegment.endStr}，距离: ${minDistance}天，价格: ${closestSegment.price}`
						);
					return closestSegment.price;
				} else if (closestSegment) {
					console.log(`找到时间段但距离太远(${minDistance}天)，不使用此时间段`);
				}

				// 4. 如果找不到合适的时间段，使用默认价格
				console.log(`没有找到合适的时间段，使用默认价格`);
				return this.getCarDefaultPrice(trimmedCarName);
			},

			// 获取酒店默认价格
			getHotelDefaultPrice(hotelName) {
				if (!hotelName) {
					console.log("获取酒店默认价格：酒店名为空");
					return 300;
				}

				console.log(`获取酒店默认价格: ${hotelName}`);

				const trimmedHotelName = hotelName.trim();

				// 查找该酒店的所有记录
				const sameNameHotels = this.allHotels.filter(hotel => hotel.name === trimmedHotelName);

				if (sameNameHotels.length > 0) {
					// 计算平均价格
					let total = 0;
					let count = 0;
					const prices = [];

					for (const hotel of sameNameHotels) {
						const price = parseFloat(hotel.price || 0);
						if (price > 0) {
							total += price;
							count++;
							prices.push(price);
						}
					}

					if (count > 0) {
						// 如果有多个价格，计算中位数
						let avgPrice = total / count;

						if (prices.length > 2) {
							prices.sort((a, b) => a - b);
							const median = prices[Math.floor(prices.length / 2)];
							console.log(`酒店 ${trimmedHotelName} 平均价格: ${avgPrice}，中位数: ${median}`);

							// 如果平均值和中位数差异较大，使用中位数
							if (Math.abs(avgPrice - median) / median > 0.3) {
								console.log(`差异较大，使用中位数: ${median}`);
								avgPrice = median;
							}
						}

						console.log(`酒店 ${trimmedHotelName} 最终默认价格: ${avgPrice}`);
						return avgPrice;
					}
				}

				console.log(`使用标准默认价格: 300`);
				return 300;
			},

			// 获取车型默认价格
			getCarDefaultPrice(carName) {
				if (!carName) {
					console.log("获取车型默认价格：车型名为空");
					return 0;
				}

				console.log(`获取车型默认价格: ${carName}`);

				const trimmedCarName = carName.trim();

				// 查找该车型的所有记录
				const sameNameCars = this.allCarTypes.filter(car => car.name === trimmedCarName);

				if (sameNameCars.length > 0) {
					// 计算平均价格
					let total = 0;
					let count = 0;
					const prices = [];

					for (const car of sameNameCars) {
						const price = parseFloat(car.pricePerDay || 0);
						if (price > 0) {
							total += price;
							count++;
							prices.push(price);
						}
					}

					if (count > 0) {
						// 如果有多个价格，计算中位数
						let avgPrice = total / count;

						if (prices.length > 2) {
							prices.sort((a, b) => a - b);
							const median = prices[Math.floor(prices.length / 2)];
							console.log(`车型 ${trimmedCarName} 平均价格: ${avgPrice}，中位数: ${median}`);

							// 如果平均值和中位数差异较大，使用中位数
							if (Math.abs(avgPrice - median) / median > 0.3) {
								console.log(`差异较大，使用中位数: ${median}`);
								avgPrice = median;
							}
						}

						console.log(`车型 ${trimmedCarName} 最终默认价格: ${avgPrice}`);
						return avgPrice;
					}
				}

				console.log(`使用标准默认价格: 0`);
				return 0;
			},

			// 获取酒店实际价格
			getHotelActualPriceByDate(hotelName, date, dayNumber) {
				console.log(`获取酒店实际价格: ${hotelName}, 日期: ${date}, 第${dayNumber}天`);

				if (!hotelName || hotelName.trim() === '' || !date) {
					console.log("获取酒店价格失败：酒店名或日期为空");
					return 0;
				}

				const price = this.getHotelPriceByDate(hotelName, date);
				console.log(`酒店 ${hotelName} 在日期 ${date} 的价格: ${price}`);
				return price;
			},

			// 计算车辆租赁费用总计
			// 修改 calculateCarTotal 方法，确保计算正确
			calculateCarTotal() {
				if (!this.form.carType) {
					console.log("未选择车型，车辆费用为0");
					return 0;
				}

				const car = this.carTypes.find(item => item.id === this.form.carType);
				if (!car || !car.name) {
					console.log("未找到车型信息，车辆费用为0");
					return 0;
				}

				console.log(`计算车型 ${car.name} 的费用，天数: ${this.form.carDays || 1}`);

				// 获取行程开始日期
				const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
				let total = 0;

				// 按天计算车辆费用
				for (let i = 0; i < (this.form.carDays || 1); i++) {
					const currentDate = startDate.add(i, 'day').format('YYYY-MM-DD');
					const dailyPrice = this.getCarPriceByDate(car.name, currentDate);
					console.log(`第${i+1}天车型价格: ${car.name}, 日期: ${currentDate}, 价格: ${dailyPrice}`);
					total += dailyPrice;
					console.log(`第${i+1}天累计: ${total}`);
				}

				console.log(`车型 ${car.name} 总费用: ${total}`);
				return total;
			},

			// 获取房间数量
			getRoomCount(dayIndex) {
				return this.form.dailyHotelRooms[dayIndex] || 1;
			},

			// 增加房间数量
			increaseRoom(dayIndex) {
				if (!this.form.dailyHotelRooms[dayIndex]) {
					this.$set(this.form.dailyHotelRooms, dayIndex, 1);
				}
				const current = parseInt(this.form.dailyHotelRooms[dayIndex]) || 1;
				if (current < 20) {
					this.form.dailyHotelRooms[dayIndex] = current + 1;
				}
			},

			// 减少房间数量
			decreaseRoom(dayIndex) {
				if (!this.form.dailyHotelRooms[dayIndex]) {
					this.$set(this.form.dailyHotelRooms, dayIndex, 1);
				}
				const current = parseInt(this.form.dailyHotelRooms[dayIndex]) || 1;
				if (current > 1) {
					this.form.dailyHotelRooms[dayIndex] = current - 1;
				}
			},

			// 验证房间数量
			validateRoom(dayIndex) {
				if (!this.form.dailyHotelRooms[dayIndex]) {
					this.form.dailyHotelRooms[dayIndex] = 1;
					return;
				}

				const value = parseInt(this.form.dailyHotelRooms[dayIndex]);

				if (isNaN(value) || value < 1) {
					this.form.dailyHotelRooms[dayIndex] = 1;
				} else if (value > 20) {
					this.form.dailyHotelRooms[dayIndex] = 20;
				} else {
					this.form.dailyHotelRooms[dayIndex] = value;
				}
			},

			// 计算某天酒店费用
			calculateDayHotelCost(dayIndex) {
				const hotelName = this.form.dailyHotels[dayIndex];
				if (!hotelName || hotelName.trim() === '') {
					console.log(`第${dayIndex+1}天未选择酒店，费用为0`);
					return 0;
				}

				const roomCount = this.form.dailyHotelRooms[dayIndex] || 1;
				const price = this.getHotelPriceForDay(dayIndex);

				const total = price * roomCount;
				console.log(`第${dayIndex+1}天酒店费用: ${price} × ${roomCount}间 = ${total}`);
				return total;
			},

			// 获取某天的酒店价格
			getHotelPriceForDay(dayIndex) {
				const hotelName = this.form.dailyHotels[dayIndex];
				if (!hotelName || hotelName.trim() === '') {
					console.log(`第${dayIndex+1}天未选择酒店，价格为0`);
					return 0;
				}

				// 获取行程开始时间
				const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
				const currentDate = startDate.add(dayIndex, 'day').format('YYYY-MM-DD');
				const dayNumber = dayIndex + 1;

				// 获取酒店价格
				const price = this.getHotelPriceByDate(hotelName, currentDate);
				console.log(`第${dayNumber}天酒店 ${hotelName} 在日期 ${currentDate} 的价格: ${price}`);
				return price;
			},

			// 处理酒店选择变化
			handleHotelChange(dayIndex) {
				if (this.form.dailyHotels[dayIndex] && !this.form.dailyHotelRooms[dayIndex]) {
					this.$set(this.form.dailyHotelRooms, dayIndex, 1);
				}
			},

			// 计算导游费用总计
			calculateGuideTotal() {
				if (!this.form.guideFeePerDay || this.form.guideFeePerDay <= 0) {
					return "0.00";
				}
				const days = this.form.guideDays || 1;
				const total = parseFloat(this.form.guideFeePerDay) * days;
				return total.toFixed(2);
			},

			// 计算各项费用总计
			calculateAllCosts() {
				// 计算车辆租赁费用
				let carTotal = this.calculateCarTotal();
				console.log(`车辆租赁费用总计: ${carTotal}`);

				// 计算导游费用
				let guideTotal = 0;
				if (this.form.guideFeePerDay) {
					guideTotal = parseFloat(this.form.guideFeePerDay) * (this.form.guideDays || 1);
				}
				console.log(`导游费用总计: ${guideTotal}`);

				// 计算酒店费用
				let hotelTotal = 0;
				for (let i = 0; i < Math.min(this.hotelCostDetails.length, this.form.days - 1); i++) {
					const detail = this.hotelCostDetails[i];
					if (detail && detail.total && detail.hotelName && !detail.hotelName.includes("行程结束")) {
						hotelTotal += parseFloat(detail.total) || 0;
					}
				}
				console.log(`酒店费用总计: ${hotelTotal}`);

				// 计算餐饮费用
				let mealTotal = 0;
				for (let i = 0; i < this.mealCostDetails.length; i++) {
					const detail = this.mealCostDetails[i];
					if (detail && detail.total) {
						mealTotal += parseFloat(detail.total) || 0;
					}
				}
				console.log(`餐饮费用总计: ${mealTotal}`);

				// 计算景点门票费用
				let attractionTotal = 0;
				for (let i = 0; i < this.attractionCostDetails.length; i++) {
					const detail = this.attractionCostDetails[i];
					if (detail && detail.total) {
						attractionTotal += parseFloat(detail.total) || 0;
					}
				}
				console.log(`景点门票费用总计: ${attractionTotal}`);

				// 计算总费用
				const total = carTotal + guideTotal + hotelTotal + mealTotal + attractionTotal;
				this.totalEstimatedCost = total.toFixed(2);
				console.log(`总费用估算: ${this.totalEstimatedCost}`);

				return {
					carTotal,
					guideTotal,
					hotelTotal,
					mealTotal,
					attractionTotal,
					total
				};
			},

			// 生成酒店费用明细
			generateHotelCostDetails() {
				const details = [];
				let totalCost = 0;

				// 只计算前 (days-1) 天的酒店费用，最后一天不计算
				const daysToCalculate = Math.max(0, this.form.days - 1);
				console.log(`计算酒店费用，共 ${this.form.days} 天，实际计算 ${daysToCalculate} 天`);

				// 获取行程开始时间（从选择的日期开始）
				const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
				console.log(`行程开始日期: ${startDate.format('YYYY-MM-DD')}`);

				for (let i = 0; i < daysToCalculate; i++) {
					const hotelName = this.form.dailyHotels[i];
					const currentDate = startDate.add(i, 'day').format('YYYY-MM-DD');
					const dayNumber = i + 1;
					const roomCount = this.form.dailyHotelRooms[i] || 1;

					if (hotelName && hotelName.trim() !== '') {
						// 获取酒店价格
						const price = this.getHotelPriceByDate(hotelName, currentDate);

						const hotelInfo = this.getHotelInfoByName(hotelName);

						if (price > 0) {
							const dayHotelCost = price * roomCount;
							details.push({
								day: dayNumber,
								date: currentDate,
								hotelName: hotelName,
								price: price,
								address: hotelInfo.address || "",
								description: hotelInfo.description || "",
								quantity: roomCount,
								total: dayHotelCost
							});
							totalCost += dayHotelCost;
							console.log(
								`第 ${dayNumber} 天酒店: ${hotelName}, 日期: ${currentDate}, 价格: ${price}, 房间数: ${roomCount}, 小计: ${dayHotelCost}`
							);
						} else {
							details.push({
								day: dayNumber,
								date: currentDate,
								hotelName: "未选择酒店",
								price: 0,
								address: "",
								description: "",
								quantity: 0,
								total: 0
							});
						}
					} else {
						details.push({
							day: dayNumber,
							date: currentDate,
							hotelName: "未选择酒店",
							price: 0,
							address: "",
							description: "",
							quantity: 0,
							total: 0
						});
					}
				}

				// 如果有最后一天，添加备注
				if (this.form.days > 0) {
					const lastDayDate = startDate.add(this.form.days - 1, 'day').format('YYYY-MM-DD');
					details.push({
						day: this.form.days,
						date: lastDayDate,
						hotelName: "行程结束，不安排住宿",
						price: 0,
						address: "",
						description: "",
						quantity: 0,
						total: 0
					});
				}

				this.hotelCostDetails = details;
				this.totalHotelCost = totalCost;
				console.log(`酒店总费用: ¥${totalCost}`);
				return totalCost;
			},

			// 获取酒店信息
			getHotelInfoByName(hotelName) {
				if (!hotelName) return {
					price: 0,
					address: "",
					description: ""
				};

				const hotel = this.uniqueHotels.find(item => item.name === hotelName);
				if (hotel) {
					return {
						name: hotel.name,
						price: hotel.price || 0,
						address: hotel.address || "",
						description: hotel.description || ""
					};
				}
				return {
					name: hotelName,
					price: 0,
					address: "",
					description: ""
				};
			},

			// 计算费用
			calculateCosts() {
				try {
					// 获取行程开始时间（从选择的日期开始）
					const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
					console.log(`计算费用，行程开始日期: ${startDate.format('YYYY-MM-DD')}`);

					// 计算车辆租赁费用
					let carTotal = this.calculateCarTotal();
					console.log(`车辆租赁费用总计: ${carTotal}`);

					// 计算导游费用
					let guideTotal = 0;
					if (this.form.guideFeePerDay) {
						guideTotal = parseFloat(this.form.guideFeePerDay) * (this.form.guideDays || 1);
					}
					console.log(`导游费用总计: ${guideTotal}`);

					// 生成并计算酒店费用
					this.generateHotelCostDetails();
					const hotelTotal = this.totalHotelCost;
					console.log(`酒店费用总计: ${hotelTotal}`);

					// 生成并计算餐饮费用
					this.generateMealCostDetails();
					const mealTotal = this.totalMealCost;
					console.log(`餐饮费用总计: ${mealTotal}`);

					// 生成并计算景点门票费用
					this.generateAttractionCostDetails();
					const attractionTotal = this.totalTicketFee;
					console.log(`景点门票费用总计: ${attractionTotal}`);

					// 计算总费用
					const total = carTotal + guideTotal + hotelTotal + mealTotal + attractionTotal;
					this.totalEstimatedCost = total.toFixed(2);
					console.log(`总费用估算: ${this.totalEstimatedCost}`);

					// 生成费用明细
					this.costDetails = [{
							item: "车辆租赁费",
							unitPrice: "按天计算",
							quantity: `${this.form.carDays || 1}天`,
							total: `¥${carTotal.toFixed(2)}`
						},
						{
							item: "导游服务费",
							unitPrice: `¥${parseFloat(this.form.guideFeePerDay || 0).toFixed(2)}/天`,
							quantity: `${this.form.guideDays || 1}天`,
							total: `¥${guideTotal.toFixed(2)}`
						},
						{
							item: "酒店住宿费",
							unitPrice: "详见明细",
							quantity: `${Math.max(0, this.form.days - 1)}晚`,
							total: `¥${hotelTotal.toFixed(2)}`
						},
						{
							item: "餐饮费用",
							unitPrice: "详见明细",
							quantity: `${this.form.days}天`,
							total: `¥${mealTotal.toFixed(2)}`
						},
						{
							item: "景点门票费",
							unitPrice: "详见明细",
							quantity: "-",
							total: `¥${attractionTotal.toFixed(2)}`
						}
					];

				} catch (error) {
					console.error("计算费用时出错:", error);
					this.totalEstimatedCost = "0.00";
				}
			},

			// 加载车型数据，处理时间段
			loadCarTypes() {
				if (this.allCarTypes.length > 0) return;
				this.loadingCarTypes = true;
				request.get("/goods/findAll")
					.then(response => {
						if (response.data) {
							// 存储所有车型数据
							this.allCarTypes = response.data.map(item => ({
								id: item.id,
								name: item.tradeName,
								pricePerDay: parseFloat(item.points) || 0,
								description: item.description || "",
								obtainingTime: item.obtainingTime || ""
							}));

							console.log("原始车型数据:", this.allCarTypes);

							// 处理车型时间段
							this.processCarTimeSegments();

							// 对车型名称进行去重
							this.createUniqueCarTypeList();

							console.log("去重后的车型列表:", this.carTypes);
							console.log("车型时间段映射:", this.carTimeSegments);
						}
					})
					.catch(error => {
						console.error("加载车型数据失败:", error);
						ElMessage.error("加载车型数据失败");
					})
					.finally(() => {
						this.loadingCarTypes = false;
					});
			},

			// 创建去重后的车型列表
			createUniqueCarTypeList() {
				if (!this.allCarTypes || this.allCarTypes.length === 0) {
					this.carTypes = [];
					return;
				}

				// 使用 Map 来去重，key 是车型名称
				const carNameMap = new Map();

				this.allCarTypes.forEach(car => {
					if (!car.name || car.name.trim() === '') {
						console.log("发现无名车型，跳过:", car);
						return;
					}

					const carName = car.name.trim();
					const existingCar = carNameMap.get(carName);

					if (!existingCar) {
						// 如果还没有这个车型名称，添加
						carNameMap.set(carName, {
							id: car.id,
							name: carName,
							pricePerDay: car.pricePerDay || 0,
							description: car.description || ""
						});
					} else {
						// 如果已存在，比较价格，选择最新（id更大）的记录
						if (car.id > existingCar.id) {
							carNameMap.set(carName, {
								id: car.id,
								name: carName,
								pricePerDay: car.pricePerDay || existingCar.pricePerDay,
								description: car.description || existingCar.description
							});
						}
					}
				});

				// 转换为数组并按名称排序
				this.carTypes = Array.from(carNameMap.values()).sort((a, b) =>
					a.name.localeCompare(b.name, 'zh-CN')
				);

				console.log(`车型去重完成: ${this.allCarTypes.length} 条记录去重为 ${this.carTypes.length} 个车型名称`);
			},

			// 加载酒店数据，处理时间段
			loadHotels() {
				if (this.allHotels.length > 0) return;
				this.loadingHotels = true;
				request.get("/signup/find", {
					params: {
						pageNum: 1,
						pageSize: 100,
						search: ""
					}
				}).then(response => {
					console.log("酒店数据响应:", response);
					if (response.data && response.data.records) {
						// 保存所有酒店数据
						this.allHotels = response.data.records.map(item => ({
							id: item.id,
							name: item.name || "未知酒店",
							price: parseFloat(item.projectName) || 0,
							address: item.address || "",
							description: item.description || "",
							obtainingTime: item.obtainingTime || ""
						}));

						console.log("原始酒店数据:", this.allHotels);

						// 处理酒店时间段
						this.processHotelTimeSegments();

						// 对酒店名称进行去重
						this.createUniqueHotelList();

						console.log("去重后的酒店名称列表:", this.uniqueHotels);
						console.log(`酒店数据加载完成: ${this.allHotels.length} 条记录, ${this.uniqueHotels.length} 个不重复名称`);
					} else {
						console.warn("酒店数据为空");
						this.allHotels = [];
						this.uniqueHotels = [];
					}
				}).catch(error => {
					console.error("加载酒店数据失败:", error);
					ElMessage.error("加载酒店数据失败");
				}).finally(() => {
					this.loadingHotels = false;
				});
			},

			// 创建去重后的酒店列表
			createUniqueHotelList() {
				if (!this.allHotels || this.allHotels.length === 0) {
					this.uniqueHotels = [];
					return;
				}

				// 使用 Map 来去重，key 是酒店名称
				const hotelNameMap = new Map();

				this.allHotels.forEach(hotel => {
					if (!hotel.name || hotel.name.trim() === '') {
						console.log("发现无名酒店，跳过:", hotel);
						return;
					}

					const hotelName = hotel.name.trim();
					const existingHotel = hotelNameMap.get(hotelName);

					if (!existingHotel) {
						// 如果还没有这个酒店名称，添加
						hotelNameMap.set(hotelName, {
							name: hotelName,
							price: hotel.price || 0,
							address: hotel.address || "",
							description: hotel.description || ""
						});
					} else {
						// 如果已存在，比较价格，选择最新（id更大）的记录
						if (hotel.id > existingHotel.id) {
							hotelNameMap.set(hotelName, {
								name: hotelName,
								price: hotel.price || existingHotel.price,
								address: hotel.address || existingHotel.address,
								description: hotel.description || existingHotel.description
							});
						}
					}
				});

				// 转换为数组并按名称排序
				this.uniqueHotels = Array.from(hotelNameMap.values()).sort((a, b) =>
					a.name.localeCompare(b.name, 'zh-CN')
				);

				console.log(`酒店去重完成: ${this.allHotels.length} 条记录去重为 ${this.uniqueHotels.length} 个酒店名称`);
			},

			// 以下是原有方法，保持不变
			disabledDate(time) {
				return time.getTime() < Date.now() - 24 * 60 * 60 * 1000;
			},

			handleStartDateChange() {
				console.log("行程开始日期改为:", this.form.startDate);
				if (this.schemeGenerated) {
					this.calculateCosts();
				}
			},

			getTripDateRange() {
				if (!this.form.startDate || this.form.days <= 0) return "";
				const start = this.form.startDate;
				const end = dayjs(this.form.startDate).add(this.form.days - 1, 'day').format('YYYY-MM-DD');
				if (this.form.days === 1) {
					return start;
				}
				return `${start} 至 ${end}`;
			},

			getDayDate(day) {
				if (!this.form.startDate) return "";
				return dayjs(this.form.startDate).add(day - 1, 'day').format('YYYY-MM-DD');
			},

			formatDescription(description) {
				return description;
			},

			formatDescriptionPreview(description) {
				if (Array.isArray(description)) {
					return description.slice(0, 2).join(' | ');
				}
				return description;
			},

			filterLocations(query) {
				this.searchQuery = query;
			},

			filterCarTypes(query) {
				this.carTypeSearchQuery = query;
			},

			filterHotels(query) {
				this.hotelSearchQuery = query;
			},

			filterAttractions(query, item) {
				this.attractionSearchQuery = query;
				return item.label.toLowerCase().includes(query.toLowerCase());
			},

			decreaseDays() {
				if (this.form.days > 1) {
					this.form.days--;
					this.handleDaysChange(this.form.days, this.form.days + 1);
				}
			},

			increaseDays() {
				if (this.form.days < 30) {
					this.form.days++;
					this.handleDaysChange(this.form.days, this.form.days - 1);
				}
			},

			validateDays() {
				let value = parseInt(this.form.days);
				if (isNaN(value) || value < 1) {
					value = 1;
				} else if (value > 30) {
					value = 30;
				}
				if (value !== this.form.days) {
					const oldVal = this.form.days;
					this.form.days = value;
					this.handleDaysChange(value, oldVal);
				}
			},

			handleDaysChange(newVal, oldVal) {
				newVal = parseInt(newVal);
				if (newVal < 1) {
					newVal = 1;
					this.form.days = 1;
				}
				if (newVal > 30) {
					newVal = 30;
					this.form.days = 30;
				}
				if (newVal === oldVal) {
					return;
				}
				if (!Array.isArray(this.form.dailyLocations)) {
					this.form.dailyLocations = [];
				}
				if (!Array.isArray(this.form.dailyHotels)) {
					this.form.dailyHotels = [];
				}
				if (!Array.isArray(this.form.dailyHotelRooms)) {
					this.form.dailyHotelRooms = [];
				}
				if (!Array.isArray(this.form.dailyMeals)) {
					this.form.dailyMeals = [
						['breakfast', 'lunch', 'dinner']
					];
				}
				const currentLocationsLength = this.form.dailyLocations.length;
				const currentHotelsLength = this.form.dailyHotels.length;
				const currentHotelRoomsLength = this.form.dailyHotelRooms.length;
				const currentMealsLength = this.form.dailyMeals.length;

				if (newVal > currentLocationsLength) {
					const itemsToAdd = newVal - currentLocationsLength;
					for (let i = 0; i < itemsToAdd; i++) {
						this.form.dailyLocations.push(null);
					}
				} else if (newVal < currentLocationsLength) {
					const itemsToRemove = currentLocationsLength - newVal;
					this.form.dailyLocations.splice(newVal, itemsToRemove);
				}

				if (newVal > currentHotelsLength) {
					const itemsToAdd = newVal - currentHotelsLength;
					for (let i = 0; i < itemsToAdd; i++) {
						this.form.dailyHotels.push("");
						this.form.dailyHotelRooms.push(1);
					}
				} else if (newVal < currentHotelsLength) {
					const itemsToRemove = currentHotelsLength - newVal;
					this.form.dailyHotels.splice(newVal, itemsToRemove);
				}

				// 确保酒店房间数组与酒店数组长度一致
				if (newVal > currentHotelRoomsLength) {
					const itemsToAdd = newVal - currentHotelRoomsLength;
					for (let i = 0; i < itemsToAdd; i++) {
						this.form.dailyHotelRooms.push(1);
					}
				} else if (newVal < currentHotelRoomsLength) {
					const itemsToRemove = currentHotelRoomsLength - newVal;
					this.form.dailyHotelRooms.splice(newVal, itemsToRemove);
				}

				if (newVal > currentMealsLength) {
					const itemsToAdd = newVal - currentMealsLength;
					for (let i = 0; i < itemsToAdd; i++) {
						this.form.dailyMeals.push(['breakfast', 'lunch', 'dinner']);
					}
				} else if (newVal < currentMealsLength) {
					const itemsToRemove = currentMealsLength - newVal;
					this.form.dailyMeals.splice(newVal, itemsToRemove);
				}
			},

			isLocationSelected(locationId, currentDay) {
				return this.form.dailyLocations.some((id, index) =>
					id === locationId && index !== currentDay
				);
			},

			// 检查酒店是否可用
			isHotelAvailable(currentDay, hotelName) {
				if (!hotelName || hotelName.trim() === '') return true;
				return !this.form.dailyHotels.some((name, index) =>
					name === hotelName && index !== currentDay
				);
			},

			getSelectedLocation(dayIndex) {
				const locationId = this.form.dailyLocations[dayIndex];
				if (!locationId) return null;
				return this.locationList.find(item => item.id === locationId) || null;
			},

			// 修改获取已选酒店信息
			getSelectedHotel(dayIndex) {
				const hotelName = this.form.dailyHotels[dayIndex];
				if (!hotelName || hotelName.trim() === '') return null;
				return this.uniqueHotels.find(item => item.name === hotelName) || null;
			},

			selectAllAttractions() {
				this.form.selectedAttractions = this.attractionList.map(item => item.id);
				ElMessage.success(`已选择全部 ${this.attractionList.length} 个景点`);
			},

			clearAllAttractions() {
				this.form.selectedAttractions = [];
				ElMessage.info('已清空景点选择');
			},

			resetForm() {
				this.form = {
					startDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
					days: 1,
					dailyLocations: [null],
					dailyHotels: [],
					dailyHotelRooms: [1],
					dailyMeals: [
						['breakfast', 'lunch', 'dinner']
					],
					attractionsPerDay: 1,
					selectedAttractions: [],
					hotelRecommendationCount: 1,
					guideFeePerDay: 500,
					guideDays: 1,
					carType: null,
					carDays: 1
				};

				this.schemeGenerated = false;
				this.generatedScheme = null;
				this.totalMealCost = 0;
				this.totalHotelCost = 0;
				this.mealCostDetails = [];
				this.hotelCostDetails = [];
				this.attractionCostDetails = [];
				this.searchQuery = "";
				this.carTypeSearchQuery = "";
				this.attractionSearchQuery = "";
				this.hotelSearchQuery = "";
				this.selectedHotels = [];
				this.selectedAttractionsInfo = [];

				ElMessage.success('表单已重置');
			},

			loadMealPrices() {
				request.get("/pointlist/find", {
					params: {
						pageNum: 1,
						pageSize: 100,
						search: ""
					}
				}).then(response => {
					console.log("餐饮价格响应:", response);
					if (response.data && response.data.records) {
						const mealTypeMap = {
							'早餐': 'breakfast',
							'午餐': 'lunch',
							'晚餐': 'dinner'
						};
						response.data.records.forEach(item => {
							if (item.name && item.points) {
								const englishKey = mealTypeMap[item.name];
								if (englishKey) {
									this.mealPrices[englishKey] = parseFloat(item.points);
								}
							}
						});
						console.log("餐饮价格加载完成:", this.mealPrices);
					}
				}).catch(error => {
					console.error("加载餐饮价格失败:", error);
				});
			},

			calculateDailyMealCost(dayIndex) {
				if (!this.form.dailyMeals || !this.form.dailyMeals[dayIndex]) {
					return 0;
				}

				let cost = 0;
				const dailyMeals = this.form.dailyMeals[dayIndex];

				if (dailyMeals.includes('breakfast')) {
					cost += this.mealPrices.breakfast || 0;
				}
				if (dailyMeals.includes('lunch')) {
					cost += this.mealPrices.lunch || 0;
				}
				if (dailyMeals.includes('dinner')) {
					cost += this.mealPrices.dinner || 0;
				}

				return cost;
			},

			calculateTotalMealCost() {
				let totalCost = 0;
				for (let i = 0; i < this.form.days; i++) {
					totalCost += this.calculateDailyMealCost(i);
				}
				return totalCost;
			},

			generateMealCostDetails() {
				const details = [];
				let totalCost = 0;

				// 行程从选择的日期开始
				const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
				console.log(`计算餐饮费用，行程开始日期: ${startDate.format('YYYY-MM-DD')}`);

				for (let i = 0; i < this.form.days; i++) {
					const currentDate = startDate.add(i, 'day').format('YYYY-MM-DD');
					const dailyMeals = this.form.dailyMeals[i] || ['breakfast', 'lunch', 'dinner'];
					const breakfastIncluded = dailyMeals.includes('breakfast');
					const lunchIncluded = dailyMeals.includes('lunch');
					const dinnerIncluded = dailyMeals.includes('dinner');

					let dayCost = 0;
					if (breakfastIncluded) dayCost += parseFloat(this.mealPrices.breakfast || 0);
					if (lunchIncluded) dayCost += parseFloat(this.mealPrices.lunch || 0);
					if (dinnerIncluded) dayCost += parseFloat(this.mealPrices.dinner || 0);

					// 添加每天的数据
					details.push({
						day: i + 1,
						date: currentDate,
						breakfast: {
							included: breakfastIncluded,
							price: parseFloat(this.mealPrices.breakfast || 0)
						},
						lunch: {
							included: lunchIncluded,
							price: parseFloat(this.mealPrices.lunch || 0)
						},
						dinner: {
							included: dinnerIncluded,
							price: parseFloat(this.mealPrices.dinner || 0)
						},
						total: dayCost
					});

					totalCost += dayCost;
				}

				console.log("餐饮费用明细:", details);
				this.mealCostDetails = details;
				this.totalMealCost = totalCost;
				return totalCost;
			},

			// 生成景点费用明细
			generateAttractionCostDetails() {
				const details = [];
				let totalCost = 0;

				if (!this.generatedScheme || !this.generatedScheme.days) {
					console.log("行程数据未生成，无法计算景点费用");
					this.attractionCostDetails = details;
					this.totalTicketFee = 0;
					return 0;
				}

				// 行程从选择的日期开始
				const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
				console.log(`计算景点费用，行程开始日期: ${startDate.format('YYYY-MM-DD')}`);

				for (let i = 0; i < this.generatedScheme.days.length; i++) {
					const day = this.generatedScheme.days[i];
					const currentDate = startDate.add(i, 'day').format('YYYY-MM-DD');
					const dayAttractions = [];
					let dayCost = 0;

					if (day.recommendations && day.recommendations.length > 0) {
						day.recommendations.forEach(recommendation => {
							if (recommendation.name !== "无景点安排" && recommendation.price > 0) {
								const price = parseFloat(recommendation.price || 0);
								dayCost += price;
								dayAttractions.push({
									name: recommendation.name || "未知景点",
									price: price,
									image: recommendation.image || ""
								});
							}
						});
					}

					// 只有当有景点时才添加到明细
					if (dayAttractions.length > 0) {
						details.push({
							day: i + 1,
							date: currentDate,
							attractions: dayAttractions,
							total: dayCost
						});
						totalCost += dayCost;
					} else {
						details.push({
							day: i + 1,
							date: currentDate,
							attractions: [],
							total: 0
						});
					}
				}

				console.log("景点费用明细:", details);
				this.attractionCostDetails = details;
				this.totalTicketFee = totalCost;
				return totalCost;
			},

			// 获取每天分配的景点
			getDayAttractions(dayIndex) {
				if (!this.generatedScheme || !this.generatedScheme.days || !this.generatedScheme.days[dayIndex]) {
					return [];
				}
				return this.generatedScheme.days[dayIndex].recommendations || [];
			},

			loadLocations() {
				if (this.locationList.length > 0) return;
				this.loadingLocations = true;
				request.get("/competition/findAll")
					.then(response => {
						if (response.data) {
							this.locationList = response.data.map(item => ({
								id: item.id,
								name: item.title,
								address: item.address,
								picture: item.competitionPictures,
								description: item.address ? item.address.split('\n').filter(line => line
									.trim() !== '') : [],
								ticketPrice: parseFloat(item.price) || 0
							}));
							this.groupLocations();
						}
					})
					.catch(error => {
						console.error("加载地点数据失败:", error);
						ElMessage.error("加载地点数据失败");
					})
					.finally(() => {
						this.loadingLocations = false;
					});
			},

			groupLocations() {
				const groups = {};
				this.locationList.forEach(location => {
					if (!location.name) {
						this.addToGroup(groups, "其他", location);
						return;
					}
					const groupLabel = this.extractGroupLabel(location.name);
					if (!groups[groupLabel]) {
						groups[groupLabel] = [];
					}
					groups[groupLabel].push(location);
				});
				const processedGroups = this.postProcessGroups(groups);
				this.locationGroups = Object.keys(processedGroups)
					.sort((a, b) => a.localeCompare(b, 'zh-CN'))
					.map(key => ({
						label: key,
						options: processedGroups[key]
					}));
			},

			extractGroupLabel(name) {
				if (!name) return "其他";
				const normalized = this.normalizeName(name);
				const firstLocation = this.extractFirstLocation(normalized);
				return this.determineLabelLength(firstLocation);
			},

			normalizeName(name) {
				if (!name) return "";
				let normalized = name.trim();
				normalized = normalized.replace(/[—-]+/g, '---');
				return normalized;
			},

			extractFirstLocation(name) {
				if (!name) return "";
				if (name.includes("---")) {
					return name.split("---")[0].trim();
				}
				return name;
			},

			determineLabelLength(location) {
				if (!location) return "其他";
				if (location.length >= 2 && location.length <= 4) {
					return location;
				}
				if (location.length > 4) {
					const recognizedPrefix = this.recognizeLocationPrefix(location);
					if (recognizedPrefix) {
						return recognizedPrefix;
					}
					return this.extractMeaningfulPrefix(location);
				}
				if (location.length === 1) {
					return this.expandSingleCharacter(location);
				}
				return "其他";
			},

			recognizeLocationPrefix(location) {
				const patterns = [
					/^(.{2,3}市)/,
					/^(.{2,3}省)/,
					/^(.{2,4}自治区)/,
					/^(.{2,4}特别行政区)/,
					/^(.{2,4}景区)/,
					/^(.{2,4}公园)/,
					/^(.{2,4}古镇)/,
					/^(.{2,4}古城)/
				];
				for (const pattern of patterns) {
					const match = location.match(pattern);
					if (match) {
						return match[1];
					}
				}
				return null;
			},

			extractMeaningfulPrefix(location) {
				if (!location || location.length === 0) return "其他";
				if (location.length >= 2) {
					const twoChar = location.substring(0, 2);
					if (this.isMeaningfulPrefix(twoChar)) {
						return twoChar;
					}
				}
				if (location.length >= 3) {
					const threeChar = location.substring(0, 3);
					if (this.isMeaningfulPrefix(threeChar)) {
						return threeChar;
					}
				}
				if (location.length >= 4) {
					const fourChar = location.substring(0, 4);
					if (this.isMeaningfulPrefix(fourChar)) {
						return fourChar;
					}
				}
				return location.length >= 2 ? location.substring(0, 2) : location;
			},

			isMeaningfulPrefix(prefix) {
				if (!prefix || prefix.length === 0) return false;
				const meaningfulPrefixes = [
					"北京", "上海", "广州", "深圳", "杭州", "南京", "苏州",
					"成都", "重庆", "西安", "武汉", "天津", "郑州", "长沙",
					"沈阳", "青岛", "大连", "宁波", "厦门", "福州", "无锡",
					"东莞", "佛山", "合肥", "昆明", "哈尔滨", "长春", "石家庄",
					"济南", "太原", "南宁", "乌鲁木齐", "兰州", "银川", "西宁",
					"敦煌", "桂林", "黄山", "张家界", "丽江", "大理", "西双版纳",
					"嘉峪关", "张掖", "莫高窟", "鸣沙山", "月牙泉", "七彩丹霞"
				];
				return meaningfulPrefixes.includes(prefix);
			},

			expandSingleCharacter(character) {
				const singleCharMap = {
					"京": "北京",
					"沪": "上海",
					"穗": "广州",
					"深": "深圳",
					"杭": "杭州",
					"宁": "南京",
					"苏": "苏州",
					"蓉": "成都",
					"渝": "重庆",
					"汉": "武汉",
					"津": "天津",
					"郑": "郑州",
					"沈": "沈阳",
					"青": "青岛",
					"甬": "宁波",
					"厦": "厦门",
					"榕": "福州",
					"锡": "无锡",
					"莞": "东莞",
					"禅": "佛山",
					"肥": "合肥",
					"昆": "昆明",
					"哈": "哈尔滨",
					"长": "长春",
					"济": "济南",
					"太": "太原",
					"邕": "南宁",
					"兰": "兰州",
					"银": "银川"
				};
				return singleCharMap[character] || character;
			},

			postProcessGroups(groups) {
				const groupKeys = Object.keys(groups);
				const mergedGroups = {};
				const processed = new Set();
				for (let i = 0; i < groupKeys.length; i++) {
					const key1 = groupKeys[i];
					if (processed.has(key1)) continue;
					if (!mergedGroups[key1]) {
						mergedGroups[key1] = groups[key1];
					}
					for (let j = i + 1; j < groupKeys.length; j++) {
						const key2 = groupKeys[j];
						if (processed.has(key2)) continue;
						if (this.shouldMergeGroups(key1, key2)) {
							mergedGroups[key1] = mergedGroups[key1].concat(groups[key2]);
							processed.add(key2);
						}
					}
					processed.add(key1);
				}
				groupKeys.forEach(key => {
					if (!processed.has(key)) {
						mergedGroups[key] = groups[key];
					}
				});
				return mergedGroups;
			},

			shouldMergeGroups(label1, label2) {
				if (!label1 || !label2 || label1 === label2) return false;
				if (label1.includes(label2) || label2.includes(label1)) {
					return true;
				}
				const similarity = this.calculateSimilarity(label1, label2);
				if (similarity > 0.7) {
					return true;
				}
				return false;
			},

			calculateSimilarity(str1, str2) {
				if (!str1 || !str2) return 0;
				const len1 = str1.length;
				const len2 = str2.length;
				if (len1 === 0 || len2 === 0) return 0;
				const matrix = [];
				for (let i = 0; i <= len1; i++) {
					matrix[i] = [i];
				}
				for (let j = 0; j <= len2; j++) {
					matrix[0][j] = j;
				}
				for (let i = 1; i <= len1; i++) {
					for (let j = 1; j <= len2; j++) {
						const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
						matrix[i][j] = Math.min(
							matrix[i - 1][j] + 1,
							matrix[i][j - 1] + 1,
							matrix[i - 1][j - 1] + cost
						);
					}
				}
				const maxLength = Math.max(len1, len2);
				const distance = matrix[len1][len2];
				const similarity = 1 - distance / maxLength;
				return similarity;
			},

			addToGroup(groups, label, location) {
				if (!groups[label]) {
					groups[label] = [];
				}
				groups[label].push(location);
			},

			loadAttractions() {
				if (this.attractionList.length > 0) return;
				this.loadingAttractions = true;
				request.get("/publicize/findAll")
					.then(response => {
						if (response.data) {
							this.attractionList = response.data.map(item => ({
								id: item.id,
								name: item.className,
								description: item.introduction ? item.introduction.split('\n').filter(
									line => line.trim() !== '') : [],
								picture: item.pictures || "https://via.placeholder.com/300x200?text=景点图片",
								ticketPrice: parseFloat(item.price) || 0,
								location: item.address
							}));
						}
					})
					.catch(error => {
						console.error("加载景点数据失败:", error);
						ElMessage.error("加载景点数据失败");
					})
					.finally(() => {
						this.loadingAttractions = false;
					});
			},

			generateScheme() {
				if (!this.validateForm()) return;

				this.generatingScheme = true;

				try {
					// 重置统计数据
					this.totalAttractions = 0;
					this.totalTicketFee = 0;
					this.totalHotelCost = 0;
					this.totalMealCost = 0;
					this.totalEstimatedCost = 0;

					// 获取已选景点
					const sortedAttractions = this.attractionList
						.filter(a => this.form.selectedAttractions.includes(a.id))
						.sort((a, b) =>
							this.form.selectedAttractions.indexOf(a.id) - this.form.selectedAttractions.indexOf(b.id)
						);

					console.log("已选景点数量:", sortedAttractions.length);
					console.log("已选景点列表:", sortedAttractions);

					// 更新景点总数
					this.totalAttractions = sortedAttractions.length;

					// 行程从选择的日期开始
					const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
					console.log(`行程开始日期: ${startDate.format('YYYY-MM-DD')}`);

					this.generatedScheme = {
						days: []
					};

					let attractionIndex = 0;
					const days = this.form.days;

					// 计算每天分配的景点数
					const attractionsPerDay = Math.ceil(sortedAttractions.length / days);
					console.log("总天数:", days, "总景点数:", sortedAttractions.length, "每天景点数:", attractionsPerDay);

					// 生成每天的行程
					for (let i = 0; i < days; i++) {
						// 获取当天选择的地点和酒店
						const locationId = this.form.dailyLocations[i];
						const hotelName = this.form.dailyHotels[i];
						const roomCount = this.form.dailyHotelRooms[i] || 1;

						const locationInfo = this.locationList.find(item => item.id === locationId) || {
							name: `地点 ${i + 1}`,
							description: '暂无描述',
							address: '未知地址',
							ticketPrice: 0,
							picture: ""
						};

						const hotelInfo = this.uniqueHotels.find(item => item.name === hotelName) || {
							name: "未选择酒店",
							price: 300,
							address: "",
							description: ""
						};

						// 获取当天餐饮安排
						const dailyMeals = this.form.dailyMeals[i] || ['breakfast', 'lunch', 'dinner'];

						// 获取当天的景点
						const dayAttractions = [];
						let remainingDays = days - i;
						let remainingAttractions = sortedAttractions.length - attractionIndex;

						// 计算今天应该分配多少景点
						let todayAttractionCount = 0;
						if (remainingAttractions > 0) {
							// 平均分配，但确保最后几天不会没有景点
							if (remainingAttractions <= remainingDays) {
								todayAttractionCount = 1;
							} else {
								todayAttractionCount = Math.min(
									attractionsPerDay,
									remainingAttractions - (remainingDays - 1)
								);
							}

							// 获取景点
							for (let j = 0; j < todayAttractionCount && attractionIndex < sortedAttractions.length; j++) {
								dayAttractions.push(sortedAttractions[attractionIndex]);
								attractionIndex++;
							}
						}

						console.log(`第${i+1}天分配景点: ${dayAttractions.length}个`, dayAttractions);

						// 生成餐饮数据
						const meals = this.generateMealData(dailyMeals);

						// 生成景点推荐
						const recommendations = this.generateRecommendations(dayAttractions);

						// 获取实际酒店价格
						const currentDate = startDate.add(i, 'day').format('YYYY-MM-DD');
						const actualHotelPrice = hotelInfo.name !== "未选择酒店" ?
							this.getHotelPriceByDate(hotelInfo.name, currentDate) :
							0;

						// 创建当天行程
						const day = {
							dayNumber: i + 1,
							date: startDate.add(i, 'day').format('YYYY-MM-DD'),
							locationInfo: {
								...locationInfo,
								picture: locationInfo.picture || ""
							},
							meals: meals,
							transport: {
								description: "全程汽车接送",
							},
							recommendations: recommendations
						};

						// 如果不是最后一天，添加酒店信息
						if (i < days - 1) {
							day.hotel = {
								name: hotelInfo.name,
								price: actualHotelPrice || hotelInfo.price || 0,
								roomCount: roomCount,
								address: hotelInfo.address,
								description: hotelInfo.description || ""
							};
						}

						this.generatedScheme.days.push(day);
					}

					console.log("生成的行程方案:", this.generatedScheme);

					// 计算费用
					this.calculateCosts();
					this.schemeGenerated = true;
					this.activeTab = "details";
					this.generatingScheme = false;

					ElMessage.success("行程方案生成成功");
				} catch (error) {
					console.error("生成行程方案出错:", error);
					ElMessage.error("生成行程方案失败: " + error.message);
					this.generatingScheme = false;
				}
			},

			generateMealData(dailyMeals) {
				if (!Array.isArray(dailyMeals) || dailyMeals.length === 0) {
					return [];
				}

				const meals = [];
				const mealNames = {
					'breakfast': {
						name: '早餐',
						time: '约30分钟'
					},
					'lunch': {
						name: '午餐',
						time: '约1小时'
					},
					'dinner': {
						name: '晚餐',
						time: '约1小时'
					}
				};

				// 早餐
				if (dailyMeals.includes('breakfast')) {
					meals.push({
						type: 'breakfast',
						name: mealNames.breakfast.name,
						standard: '包含早餐',
						price: this.mealPrices.breakfast || 0,
						time: mealNames.breakfast.time
					});
				}

				// 午餐
				if (dailyMeals.includes('lunch')) {
					meals.push({
						type: 'lunch',
						name: mealNames.lunch.name,
						standard: '包含午餐',
						price: this.mealPrices.lunch || 0,
						time: mealNames.lunch.time
					});
				}

				// 晚餐
				if (dailyMeals.includes('dinner')) {
					meals.push({
						type: 'dinner',
						name: mealNames.dinner.name,
						standard: '包含晚餐',
						price: this.mealPrices.dinner || 0,
						time: mealNames.dinner.time
					});
				}

				return meals;
			},

			// 生成景点推荐
			generateRecommendations(attractions) {
				if (!attractions || attractions.length === 0) {
					return [{
						name: "无景点安排",
						description: "当天没有安排景点",
						price: 0,
						image: ""
					}];
				}

				return attractions.map(attraction => ({
					name: attraction.name,
					description: attraction.description,
					price: attraction.ticketPrice || 0,
					image: attraction.picture || "https://via.placeholder.com/300x200?text=景点图片"
				}));
			},
			// 验证表单
			validateForm() {
				// 验证行程开始日期
				if (!this.form.startDate) {
					ElMessage.warning("请选择行程开始日期");
					return false;
				}

				// 验证行程天数
				if (!this.form.days || this.form.days < 1 || this.form.days > 30) {
					ElMessage.warning("行程天数必须在1-30天之间");
					return false;
				}

				// 验证导游天数
				if (!this.form.guideDays || this.form.guideDays < 1) {
					ElMessage.warning("导游服务天数不能为空，且至少1天");
					return false;
				}

				if (this.form.guideDays > this.form.days) {
					ElMessage.warning(`导游服务天数不能超过行程天数(${this.form.days}天)`);
					return false;
				}

				// 验证车辆租赁天数
				if (!this.form.carDays || this.form.carDays < 1) {
					ElMessage.warning("车辆租赁天数不能为空，且至少1天");
					return false;
				}

				if (this.form.carDays > this.form.days) {
					ElMessage.warning(`车辆租赁天数不能超过行程天数(${this.form.days}天)`);
					return false;
				}

				// 验证每天的地点
				for (let i = 0; i < this.form.days; i++) {
					if (!this.form.dailyLocations[i]) {
						ElMessage.warning(`请选择第 ${i + 1} 天的行程地点`);
						return false;
					}
				}

				// 验证酒店 - 只验证前n-1天
				const hotelDays = Math.max(0, this.form.days - 1);
				for (let i = 0; i < hotelDays; i++) {
					if (!this.form.dailyHotels[i] || this.form.dailyHotels[i].trim() === "") {
						ElMessage.warning(`请选择第 ${i + 1} 天的住宿酒店`);
						return false;
					}
				}

				// 验证景点
				if (this.form.selectedAttractions.length === 0) {
					ElMessage.warning("请选择至少一个景点");
					return false;
				}

				// 验证车型
				if (!this.form.carType) {
					ElMessage.warning("请选择车型");
					return false;
				}

				return true;
			},
			// 计算费用
			// 修改 calculateCosts 方法中的车辆租赁费用部分
			calculateCosts() {
				try {
					// 获取行程开始时间（从选择的日期开始）
					const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');
					console.log(`计算费用，行程开始日期: ${startDate.format('YYYY-MM-DD')}`);

					// 计算车辆租赁费用
					let carTotal = this.calculateCarTotal();
					console.log(`车辆租赁费用总计: ${carTotal}`);

					// 获取选中的车型信息
					const selectedCar = this.carTypes.find(item => item.id === this.form.carType);
					const carName = selectedCar ? selectedCar.name : "未选择车型";
					const defaultCarPrice = selectedCar ? selectedCar.pricePerDay : 0;

					// 计算每天的车辆费用，以便显示明细
					let carDailyDetails = [];
					let carTotalFromDetails = 0;

					if (selectedCar && selectedCar.name) {
						// 按天计算车辆费用
						for (let i = 0; i < (this.form.carDays || 1); i++) {
							const currentDate = startDate.add(i, 'day').format('YYYY-MM-DD');
							const dailyPrice = this.getCarPriceByDate(selectedCar.name, currentDate);
							carDailyDetails.push({
								day: i + 1,
								date: currentDate,
								price: dailyPrice
							});
							carTotalFromDetails += dailyPrice;
						}
					}

					console.log(`车辆租赁费用明细:`, carDailyDetails);
					console.log(`车辆租赁费用总计(从明细计算): ${carTotalFromDetails}`);

					// 计算导游费用
					let guideTotal = 0;
					if (this.form.guideFeePerDay) {
						guideTotal = parseFloat(this.form.guideFeePerDay) * (this.form.guideDays || 1);
					}
					console.log(`导游费用总计: ${guideTotal}`);

					// 生成并计算酒店费用
					this.generateHotelCostDetails();
					const hotelTotal = this.totalHotelCost;
					console.log(`酒店费用总计: ${hotelTotal}`);

					// 生成并计算餐饮费用
					this.generateMealCostDetails();
					const mealTotal = this.totalMealCost;
					console.log(`餐饮费用总计: ${mealTotal}`);

					// 生成并计算景点门票费用
					this.generateAttractionCostDetails();
					const attractionTotal = this.totalTicketFee;
					console.log(`景点门票费用总计: ${attractionTotal}`);

					// 计算总费用
					const total = carTotal + guideTotal + hotelTotal + mealTotal + attractionTotal;
					this.totalEstimatedCost = total.toFixed(2);
					console.log(`总费用估算: ${this.totalEstimatedCost}`);

					// 生成费用明细
					// 修改车辆租赁费用明细的显示
					if (carDailyDetails.length > 0) {
						// 计算平均每天价格
						const avgDailyPrice = carTotal / (this.form.carDays || 1);

						// 检查价格是否一致
						let isPriceConsistent = true;
						const firstPrice = carDailyDetails[0].price;
						for (let i = 1; i < carDailyDetails.length; i++) {
							if (carDailyDetails[i].price !== firstPrice) {
								isPriceConsistent = false;
								break;
							}
						}

						if (isPriceConsistent && carDailyDetails.length > 0) {
							// 如果每天价格相同，显示固定价格
							this.costDetails = [{
								item: "车辆租赁费",
								unitPrice: `¥${firstPrice.toFixed(2)}/天`,
								quantity: `${this.form.carDays || 1}天`,
								total: `¥${carTotal.toFixed(2)}`
							}];
						} else {
							// 如果每天价格不同，显示平均价格
							this.costDetails = [{
								item: "车辆租赁费",
								unitPrice: `约¥${avgDailyPrice.toFixed(2)}/天`,
								quantity: `${this.form.carDays || 1}天`,
								total: `¥${carTotal.toFixed(2)}`
							}];
						}
					} else {
						this.costDetails = [{
							item: "车辆租赁费",
							unitPrice: "¥0.00/天",
							quantity: `${this.form.carDays || 1}天`,
							total: "¥0.00"
						}];
					}

					// 添加其他费用明细
					this.costDetails.push({
						item: "导游服务费",
						unitPrice: `¥${parseFloat(this.form.guideFeePerDay || 0).toFixed(2)}/天`,
						quantity: `${this.form.guideDays || 1}天`,
						total: `¥${guideTotal.toFixed(2)}`
					}, {
						item: "酒店住宿费",
						unitPrice: "详见明细",
						quantity: `${Math.max(0, this.form.days - 1)}晚`,
						total: `¥${hotelTotal.toFixed(2)}`
					}, {
						item: "餐饮费用",
						unitPrice: "详见明细",
						quantity: `${this.form.days}天`,
						total: `¥${mealTotal.toFixed(2)}`
					}, {
						item: "景点门票费",
						unitPrice: "详见明细",
						quantity: "-",
						total: `¥${attractionTotal.toFixed(2)}`
					});

				} catch (error) {
					console.error("计算费用时出错:", error);
					this.totalEstimatedCost = "0.00";
				}
			},
			// 新增标签页
			getCostDetailTabs() {
				return [{
						label: "总览",
						name: "overview"
					},
					{
						label: "酒店明细",
						name: "hotels"
					},
					{
						label: "餐饮明细",
						name: "meals"
					},
					{
						label: "景点明细",
						name: "attractions"
					}
				];
			},
			// 导出Word文档
			// 导出Word文档
			// 导出Word文档
			async exportItineraryToWord() {
				if (!this.schemeGenerated || !this.generatedScheme) {
					ElMessage.warning("请先生成行程方案");
					return;
				}

				const loadingInstance = ElLoading.service({
					lock: true,
					text: '正在生成Word文档...',
					background: 'rgba(0, 0, 0, 0.7)'
				});

				try {
					// 获取行程开始日期
					const startDate = this.form.startDate ? dayjs(this.form.startDate) : dayjs().add(1, 'day');

					// 计算各项费用
					const carTotal = parseFloat(this.selectedCarType.pricePerDay || 0) * (this.form.carDays || 1);
					const guideTotal = parseFloat(this.form.guideFeePerDay || 0) * (this.form.guideDays || 1);

					// 构建行程数据对象
					const exportData = {
						// 行程日期范围
						dateRange: this.tripDateRange,

						// 行程开始日期
						startDate: this.form.startDate || startDate.format('YYYY-MM-DD'),

						// 基本行程信息
						days: this.form.days || 1,
						totalAttractions: this.totalAttractions || 0,
						totalCost: this.totalEstimatedCost || "0.00",

						// 各项费用总额
						totalVehicleFee: carTotal || 0.0,
						totalGuideFee: guideTotal || 0.0,
						totalHotelFee: parseFloat(this.totalHotelCost || 0),
						totalMealFee: parseFloat(this.totalMealCost || 0),
						totalTicketFee: parseFloat(this.totalTicketFee || 0),

						// 费用明细
						costDetails: (this.costDetails || []).map(item => ({
							item: item.item || "",
							unitPrice: item.unitPrice || "",
							quantity: item.quantity || "",
							total: item.total || ""
						})),

						// 酒店明细（包含房间数）
						hotelCostDetails: (this.hotelCostDetails || [])
							.filter(hotel => hotel.hotelName && !hotel.hotelName.includes("行程结束"))
							.map(item => ({
								day: item.day || 1,
								date: item.date || "",
								hotelName: item.hotelName || "",
								price: parseFloat(item.price || 0),
								quantity: item.quantity || 1,
								address: item.address || "",
								total: parseFloat(item.total || 0)
							})),

						// 餐饮明细
						mealCostDetails: (this.mealCostDetails || []).map(item => {
							const breakfastPrice = parseFloat(this.mealPrices.breakfast || 0);
							const lunchPrice = parseFloat(this.mealPrices.lunch || 0);
							const dinnerPrice = parseFloat(this.mealPrices.dinner || 0);

							return {
								day: item.day || 1,
								date: item.date || "",
								breakfast: {
									included: item.breakfast?.included || false,
									price: breakfastPrice
								},
								lunch: {
									included: item.lunch?.included || false,
									price: lunchPrice
								},
								dinner: {
									included: item.dinner?.included || false,
									price: dinnerPrice
								},
								total: parseFloat(item.total || 0)
							};
						}),

						// 景点明细（包含景点图片）
						attractionCostDetails: (this.attractionCostDetails || [])
							.filter(day => parseFloat(day.total || 0) > 0)
							.map(item => ({
								day: item.day || 1,
								date: item.date || "",
								attractions: (item.attractions || [])
									.filter(attr => attr.name && attr.name !== "无景点安排")
									.map(attr => ({
										name: attr.name || "",
										price: parseFloat(attr.price || 0),
										image: attr.image || "" // 添加景点图片字段
									})),
								total: parseFloat(item.total || 0)
							})),

						// 行程详情
						itinerary: (this.generatedScheme.days || []).map((day, index) => {
							const dayData = {
								dayNumber: day.dayNumber || (index + 1),
								date: day.date || "",
								location: {
									name: day.locationInfo?.name || "",
									description: this.formatDescriptionForExport(day.locationInfo
										?.description),
									address: day.locationInfo?.address || "",
									ticketPrice: parseFloat(day.locationInfo?.ticketPrice || 0),
									// 新增图片字段
									image: day.locationInfo?.picture || ""
								},
								transport: {
									description: day.transport?.description || "全程汽车接送"
								},
								recommendations: []
							};

							// 处理餐饮
							if (day.meals && day.meals.length > 0) {
								day.meals.forEach(meal => {
									switch (meal.type) {
										case 'breakfast':
											dayData.breakfast = {
												adult: meal.price ? `¥${meal.price}/人` : "不含",
												child: "",
												time: meal.time || ""
											};
											break;
										case 'lunch':
											dayData.lunch = {
												adult: meal.price ? `¥${meal.price}/人` : "不含",
												child: "",
												time: meal.time || ""
											};
											break;
										case 'dinner':
											dayData.dinner = {
												adult: meal.price ? `¥${meal.price}/人` : "不含",
												child: "",
												time: meal.time || ""
											};
											break;
									}
								});
							} else {
								// 没有餐饮安排的情况
								dayData.breakfast = {
									adult: "不含",
									child: "",
									time: ""
								};
								dayData.lunch = {
									adult: "不含",
									child: "",
									time: ""
								};
								dayData.dinner = {
									adult: "不含",
									child: "",
									time: ""
								};
							}

							// 处理景点
							if (day.recommendations && day.recommendations.length > 0) {
								dayData.recommendations = day.recommendations
									.filter(rec => rec.name && rec.name !== "无景点安排")
									.map(rec => ({
										name: rec.name || "",
										description: this.formatDescriptionForExport(rec
											.description),
										price: parseFloat(rec.price || 0),
										image: rec.image || "" // 景点图片
									}));
							}

							// 如果不是最后一天，添加酒店信息
							if (index < this.form.days - 1 && day.hotel) {
								dayData.hotel = {
									name: day.hotel.name || "",
									price: parseFloat(day.hotel.price || 0),
									roomCount: day.hotel.roomCount || 1,
									address: day.hotel.address || "",
									description: day.hotel.description || ""
								};
							}

							return dayData;
						})
					};

					console.log("导出数据:", JSON.stringify(exportData, null, 2));

					// 调用后端接口导出Word文档
					const response = await request.post("/scheme/export", exportData, {
						responseType: 'blob',
						headers: {
							'Content-Type': 'application/json'
						}
					}).catch(error => {
						console.error("导出请求失败:", error);
						if (error.response) {
							if (error.response.data instanceof Blob) {
								return error.response.data.text().then(text => {
									throw new Error(text || "服务器内部错误");
								});
							} else {
								throw new Error(error.response.data?.message || error.message || "服务器内部错误");
							}
						} else {
							throw error;
						}
					});

					this.handleWordExport(response);

				} catch (error) {
					console.error("生成Word文档失败:", error);
					ElMessage.error("生成Word文档失败: " + (error.message || "未知错误"));
				} finally {
					loadingInstance.close();
				}
			},
			formatDescriptionForExport(description) {
				if (!description) {
					return "";
				}

				if (Array.isArray(description)) {
					return description.join("\n");
				}

				if (typeof description === 'string') {
					return description;
				}

				return String(description);
			},
			handleWordExport(response) {
				try {
					// 检查响应是否为 Blob
					if (!response || !(response instanceof Blob)) {
						console.error("无效的响应数据:", response);
						ElMessage.error("服务器返回的数据格式不正确");
						return;
					}

					const blob = new Blob([response], {
						type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
					});

					if (blob.size === 0) {
						console.error("下载失败: Blob大小为0");
						ElMessage.error("下载失败: 文档内容为空");
						return;
					}

					const url = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;

					// 生成文件名
					const fileName = `行程方案_${dayjs().format('YYYYMMDD_HHmmss')}.docx`;
					link.setAttribute('download', fileName);

					// 设置链接样式并添加到文档
					link.style.display = 'none';
					document.body.appendChild(link);

					// 触发点击下载
					link.click();

					// 清理资源
					setTimeout(() => {
						document.body.removeChild(link);
						window.URL.revokeObjectURL(url);
					}, 100);

					ElMessage.success("Word文档导出成功，开始下载");

				} catch (error) {
					console.error("下载处理失败:", error);
					ElMessage.error(`下载失败: ${error.message}`);
				}
			},
			formatHotelDate(dateString) {
				if (!dateString) return "";
				try {
					const date = new Date(dateString);
					if (isNaN(date.getTime())) {
						return dateString; // 返回原始字符串
					}
					return dayjs(date).format('YYYY-MM-DD');
				} catch (error) {
					console.error("格式化日期失败:", dateString, error);
					return dateString;
				}
			},
			handleError(error) {
				console.error("导出失败:", error);
				ElMessage.error("导出失败: " + (error.message || "未知错误"));
			}
		},
		mounted() {
			this.loadLocations();
			this.loadAttractions();
			this.loadCarTypes();
			this.loadHotels();
			this.loadMealPrices();
			this.form.dailyLocations = new Array(this.form.days).fill(null);
			this.form.dailyHotels = new Array(this.form.days).fill(null);
			this.form.dailyHotelRooms = new Array(this.form.days).fill(1); // 默认1间房
			this.form.dailyMeals = new Array(this.form.days).fill(['breakfast', 'lunch', 'dinner']);
		}
	};
</script>

<style scoped>
	.scheme-generator {
		display: flex;
		flex-direction: column;
		gap: 30px;
	}

	.section-title {
		font-size: 20px;
		color: #303133;
		margin-bottom: 20px;
		padding-bottom: 10px;
		border-bottom: 1px solid #ebeef5;
		font-weight: 600;
	}

	.sub-title {
		font-size: 16px;
		color: #606266;
		margin: 20px 0 15px 0;
		font-weight: 500;
	}

	.config-section {
		background: #fff;
		border-radius: 8px;
		padding: 20px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		margin-bottom: 20px;
	}

	.custom-days-control {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.control-btn {
		width: 40px;
		height: 40px;
		font-size: 18px;
		font-weight: bold;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
	}

	.control-btn:disabled {
		background-color: #f5f7fa;
		border-color: #e4e7ed;
		color: #c0c4cc;
		cursor: not-allowed;
	}

	.control-btn:not(:disabled):hover {
		background-color: #409eff;
		border-color: #409eff;
		color: white;
	}

	.days-input {
		width: 80px;
		text-align: center;
	}

	.days-input :deep(.el-input__inner) {
		text-align: center;
	}

	.daily-locations {
		margin-top: 20px;
	}

	.day-section {
		border: 1px solid #dcdfe6;
		border-radius: 8px;
		padding: 20px;
		margin-bottom: 20px;
		background: #fafafa;
		transition: all 0.3s;
	}

	.day-section:hover {
		border-color: #c0c4cc;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
	}

	.day-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
		padding-bottom: 10px;
		border-bottom: 1px solid #e4e7ed;
	}

	.day-title {
		font-size: 16px;
		font-weight: 600;
		color: #409eff;
		margin: 0;
	}

	.day-config {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.config-item {
		display: flex;
		align-items: flex-start;
		gap: 15px;
		margin-bottom: 15px;
	}

	.config-label {
		min-width: 100px;
		font-weight: 500;
		color: #606266;
		line-height: 40px;
		text-align: right;
	}

	.location-select,
	.hotel-select {
		flex: 1;
		min-width: 300px;
	}

	.location-option,
	.hotel-option {
		padding: 8px 0;
		line-height: 1.4;
	}

	.location-name,
	.hotel-name {
		font-weight: 500;
		margin-bottom: 4px;
		color: #303133;
		font-size: 14px;
	}

	.location-address,
	.hotel-address {
		font-size: 12px;
		color: #909399;
		margin-bottom: 2px;
	}

	.location-price,
	.hotel-price {
		font-size: 12px;
		color: #e6a23c;
		font-weight: 500;
	}

	.hotel-details {
		display: flex;
		justify-content: space-between;
		font-size: 12px;
		color: #909399;
		margin-bottom: 2px;
	}

	.hotel-description {
		font-size: 12px;
		color: #606266;
		margin-top: 4px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.occupied-badge {
		display: inline-block;
		padding: 2px 6px;
		background-color: #f56c6c;
		color: white;
		font-size: 12px;
		border-radius: 3px;
		margin-right: 8px;
		line-height: 1;
	}

	.selected-preview {
		flex: 1;
		max-width: 400px;
		padding: 15px;
		background: white;
		border-radius: 6px;
		border: 1px solid #e4e7ed;
		margin-top: 10px;
	}

	.preview-image {
		width: 100px;
		height: 80px;
		border-radius: 4px;
		margin-right: 15px;
		flex-shrink: 0;
	}

	.image-error {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		background: #f5f7fa;
		color: #909399;
		font-size: 12px;
	}

	.preview-info {
		flex: 1;
	}

	.preview-name {
		font-weight: 600;
		margin-bottom: 8px;
		color: #303133;
		font-size: 16px;
	}

	.preview-desc {
		font-size: 13px;
		color: #606266;
		line-height: 1.4;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.preview-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 8px;
	}

	.preview-price {
		font-size: 16px;
		font-weight: bold;
		color: #e6a23c;
	}

	.preview-address {
		font-size: 12px;
		color: #606266;
		display: flex;
		align-items: center;
		gap: 5px;
		margin: 5px 0;
	}

	.preview-address i {
		color: #909399;
	}

	.hotel-preview {
		width: 100%;
	}

	.meal-config {
		display: flex;
		align-items: center;
		gap: 15px;
	}

	.meal-arrangement {
		display: flex;
		align-items: center;
		gap: 20px;
		padding: 10px 0;
	}

	.daily-meal {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 15px;
		background: #f8f9fa;
		border-radius: 6px;
		border: 1px solid #e4e7ed;
	}

	.meal-checkbox-group {
		display: flex;
		gap: 20px;
	}

	.meal-price-info {
		font-size: 14px;
		color: #e6a23c;
		font-weight: 500;
		margin-top: 5px;
	}

	.el-form-item {
		margin-bottom: 20px;
	}

	.fee-input {
		width: 200px;
	}

	.car-select {
		width: 300px;
	}

	.car-option {
		padding: 8px 0;
	}

	.car-name {
		font-weight: 500;
		margin-bottom: 4px;
	}

	.car-details {
		font-size: 12px;
		color: #909399;
	}

	.no-data {
		padding: 10px;
		text-align: center;
		color: #909399;
		font-size: 14px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.action-buttons {
		margin-top: 30px;
		display: flex;
		gap: 15px;
		justify-content: center;
		padding-top: 20px;
		border-top: 1px solid #ebeef5;
	}

	.preview-section {
		background: #fff;
		border-radius: 8px;
		padding: 20px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		margin-top: 20px;
	}

	.overview-cards {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
		gap: 20px;
		margin-bottom: 30px;
	}

	.overview-card {
		border-radius: 8px;
		overflow: hidden;
		transition: transform 0.3s, box-shadow 0.3s;
		cursor: pointer;
	}

	.overview-card:hover {
		transform: translateY(-5px);
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
	}

	.card-content {
		display: flex;
		align-items: center;
		padding: 20px;
	}

	.card-content i {
		font-size: 36px;
		margin-right: 15px;
		color: #409eff;
	}

	.card-title {
		font-size: 14px;
		color: #909399;
		margin-bottom: 5px;
	}

	.card-value {
		font-size: 20px;
		font-weight: bold;
		color: #303133;
	}

	.preview-tabs {
		border-radius: 8px;
		overflow: hidden;
		margin-top: 20px;
	}

	.itinerary-details {
		display: flex;
		flex-direction: column;
		gap: 30px;
		padding: 20px 0;
	}

	.day-itinerary {
		border: 1px solid #e4e7ed;
		border-radius: 8px;
		overflow: hidden;
		background: #fff;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	}

	.day-header {
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		color: white;
		padding: 20px;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.day-number {
		font-size: 24px;
		font-weight: bold;
	}

	.day-date {
		font-size: 16px;
		opacity: 0.9;
	}

	.itinerary-title {
		background: #f8f9fa;
		padding: 15px 20px;
		font-size: 18px;
		font-weight: 600;
		color: #333;
		border-bottom: 1px solid #e0e0e0;
	}

	.location-description {
		padding: 15px 20px;
		background: #f9fbfd;
		border-bottom: 1px solid #e0e0e0;
	}

	.description-line {
		margin-bottom: 5px;
		color: #666;
		font-size: 14px;
		line-height: 1.5;
	}

	.no-description {
		color: #999;
		font-style: italic;
		font-size: 14px;
	}

	.itinerary-content {
		padding: 0;
	}

	.time-section {
		border-bottom: 1px solid #f0f0f0;
		padding: 20px;
		background: #fff;
	}

	.time-section:last-child {
		border-bottom: none;
	}

	.time-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 15px;
		padding-bottom: 10px;
		border-bottom: 1px solid #e0e0e0;
	}

	.time-label {
		font-weight: bold;
		color: #333;
		font-size: 16px;
	}

	.activity-type {
		font-size: 12px;
		color: white;
		background: #409eff;
		padding: 3px 10px;
		border-radius: 12px;
	}

	.time-content {
		padding: 0 10px;
	}

	.meal-section {
		display: flex;
		flex-direction: column;
		gap: 15px;
	}

	.meal-item {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 12px;
		background: #f9fbfd;
		border-radius: 8px;
		border: 1px solid #e4edf9;
	}

	.meal-title {
		display: flex;
		align-items: center;
		gap: 8px;
		font-weight: bold;
		color: #409eff;
		font-size: 15px;
	}

	.meal-title i {
		font-size: 16px;
	}

	.meal-details {
		display: flex;
		flex-direction: column;
		gap: 5px;
		font-size: 14px;
		color: #666;
	}

	.meal-details div {
		display: flex;
		align-items: center;
		gap: 5px;
	}

	.transport-info {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 10px 0;
	}

	.transport-desc {
		color: #333;
		font-size: 14px;
		line-height: 1.5;
	}

	.activity-recommendations {
		padding: 10px 0;
	}

	.recommendation-title {
		font-weight: bold;
		color: #333;
		margin-bottom: 10px;
		font-size: 15px;
	}

	.recommendation-list {
		display: flex;
		flex-direction: column;
		gap: 15px;
	}

	.recommendation-item {
		padding: 15px;
		background: #f9fbfd;
		border-radius: 8px;
		border: 1px solid #e4edf9;
	}

	.recommendation-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 10px;
	}

	.recommendation-name {
		font-weight: 600;
		font-size: 16px;
		color: #303133;
	}

	.recommendation-price {
		color: #e6a23c;
		font-weight: 500;
		font-size: 15px;
	}

	.recommendation-desc {
		color: #606266;
		font-size: 14px;
		margin-bottom: 10px;
		line-height: 1.5;
	}

	.recommendation-image-container {
		width: 100%;
		height: 200px;
		border-radius: 8px;
		overflow: hidden;
		margin-top: 10px;
	}

	.recommendation-image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.3s;
	}

	.recommendation-image:hover {
		transform: scale(1.05);
	}

	.hotel-info {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 10px 0;
	}

	.hotel-name {
		color: #333;
		font-size: 16px;
		font-weight: bold;
		margin-bottom: 5px;
	}

	.hotel-price {
		color: #e6a23c;
		font-weight: 500;
		font-size: 15px;
		margin-bottom: 5px;
	}

	.hotel-address {
		font-size: 13px;
		color: #666;
		display: flex;
		align-items: center;
		gap: 5px;
		margin-bottom: 5px;
	}

	.hotel-address i {
		color: #909399;
	}

	.hotel-desc {
		font-size: 13px;
		color: #666;
		line-height: 1.4;
		margin-top: 5px;
	}

	.cost-breakdown {
		padding: 20px 0;
	}

	.meal-details {
		padding: 20px 0;
	}

	.cost-summary {
		margin-top: 20px;
		padding: 20px;
		background: #f8f9fa;
		border-radius: 8px;
		text-align: right;
	}

	.meal-summary {
		margin-top: 20px;
		padding: 20px;
		background: #f8f9fa;
		border-radius: 8px;
		text-align: right;
	}

	.total-cost {
		font-size: 18px;
		font-weight: bold;
		color: #333;
		margin-bottom: 10px;
	}

	.total-meal-cost {
		font-size: 18px;
		font-weight: bold;
		color: #333;
		margin-bottom: 10px;
	}

	.cost-note {
		color: #666;
		font-size: 14px;
		font-style: italic;
	}

	.meal-cost-note {
		color: #666;
		font-size: 14px;
		font-style: italic;
	}

	.transfer-footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 6px 12px;
		border-top: 1px solid #ebeef5;
		font-size: 12px;
	}

	.count-text {
		color: #909399;
	}

	.no-data {
		padding: 10px;
		text-align: center;
		color: #909399;
		font-size: 14px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	/* 动画效果 */
	.day-section {
		animation: fadeIn 0.5s ease;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(20px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.overview-card {
		animation: slideIn 0.5s ease;
		animation-fill-mode: both;
	}

	.overview-card:nth-child(1) {
		animation-delay: 0.1s;
	}

	.overview-card:nth-child(2) {
		animation-delay: 0.2s;
	}

	.overview-card:nth-child(3) {
		animation-delay: 0.3s;
	}

	.overview-card:nth-child(4) {
		animation-delay: 0.4s;
	}

	@keyframes slideIn {
		from {
			opacity: 0;
			transform: translateX(-20px);
		}

		to {
			opacity: 1;
			transform: translateX(0);
		}
	}

	/* 滚动条样式 */
	:deep(.el-scrollbar__bar) {
		opacity: 0.6;
	}

	:deep(.el-scrollbar__thumb) {
		background-color: rgba(144, 147, 153, 0.3);
		transition: background-color 0.3s;
	}

	:deep(.el-scrollbar__thumb:hover) {
		background-color: rgba(144, 147, 153, 0.5);
	}

	/* 表格样式 */
	:deep(.el-table) {
		border-radius: 8px;
		overflow: hidden;
	}

	:deep(.el-table__header) {
		background: #f5f7fa;
	}

	:deep(.el-table th) {
		background: #f5f7fa;
		color: #303133;
		font-weight: 600;
	}

	:deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
		background: #fafafa;
	}

	:deep(.el-table .cell) {
		padding: 12px 10px;
	}

	/* 标签页样式 */
	:deep(.el-tabs__nav-wrap::after) {
		height: 1px;
		background-color: #e4e7ed;
	}

	:deep(.el-tabs__item) {
		font-weight: 500;
		transition: all 0.3s;
	}

	:deep(.el-tabs__item.is-active) {
		color: #409eff;
		font-weight: 600;
	}

	:deep(.el-tabs__active-bar) {
		background-color: #409eff;
		height: 3px;
	}

	/* 下拉菜单样式 */
	:deep(.el-select-dropdown__item) {
		padding: 8px 20px;
		transition: all 0.3s;
	}

	:deep(.el-select-dropdown__item:hover) {
		background-color: #f5f7fa;
	}

	:deep(.el-select-dropdown__item.is-selected) {
		background-color: #ecf5ff;
		color: #409eff;
	}

	:deep(.el-select-dropdown__item.is-disabled) {
		color: #c0c4cc;
		cursor: not-allowed;
		background-color: #fff;
	}

	/* 穿梭框样式 */
	:deep(.el-transfer) {
		display: flex;
		justify-content: center;
	}

	:deep(.el-transfer-panel) {
		width: 200px;
		height: 400px;
	}

	:deep(.el-transfer__buttons) {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 0 20px;
	}

	:deep(.el-transfer__button) {
		margin: 10px 0;
		width: 100%;
	}

	/* 加载状态 */
	:deep(.el-loading-mask) {
		background-color: rgba(255, 255, 255, 0.9);
	}

	:deep(.el-loading-spinner .circular) {
		width: 42px;
		height: 42px;
	}

	:deep(.el-loading-text) {
		margin-top: 10px;
		color: #409eff;
		font-weight: 500;
	}

	/* 图片预览样式 */
	:deep(.el-image-viewer__wrapper) {
		z-index: 9999;
	}

	:deep(.el-image-viewer__btn) {
		color: white;
		background-color: rgba(0, 0, 0, 0.5);
	}

	:deep(.el-image-viewer__btn:hover) {
		background-color: rgba(0, 0, 0, 0.8);
	}

	/* 提示信息样式 */
	:deep(.el-message) {
		z-index: 9999;
		min-width: 200px;
	}

	:deep(.el-message--success) {
		background-color: #f0f9eb;
		border-color: #e1f3d8;
		color: #67c23a;
	}

	:deep(.el-message--warning) {
		background-color: #fdf6ec;
		border-color: #faecd8;
		color: #e6a23c;
	}

	:deep(.el-message--error) {
		background-color: #fef0f0;
		border-color: #fde2e2;
		color: #f56c6c;
	}

	:deep(.el-message--info) {
		background-color: #f4f4f5;
		border-color: #e9e9eb;
		color: #909399;
	}

	/* 工具类 */
	.text-ellipsis {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.multi-line-ellipsis {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.flex-center {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.flex-between {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.flex-start {
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	.flex-end {
		display: flex;
		align-items: center;
		justify-content: flex-end;
	}

	/* 滚动条 */
	::-webkit-scrollbar {
		width: 6px;
		height: 6px;
	}

	::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 3px;
	}

	::-webkit-scrollbar-thumb {
		background: #c1c1c1;
		border-radius: 3px;
	}

	::-webkit-scrollbar-thumb:hover {
		background: #a8a8a8;
	}

	/* 打印样式 */
	@media print {

		.action-buttons,
		.el-form-item,
		.el-tabs__header,
		.el-tabs__nav-wrap {
			display: none !important;
		}

		.scheme-generator {
			gap: 0;
		}

		.config-section {
			display: none;
		}

		.preview-section {
			box-shadow: none;
			padding: 0;
		}

		.day-itinerary {
			break-inside: avoid;
			page-break-inside: avoid;
		}
	}
</style>

<style scoped>
	.scheme-generator {
		display: flex;
		flex-direction: column;
		gap: 30px;
	}

	.section-title {
		font-size: 20px;
		color: #303133;
		margin-bottom: 20px;
		padding-bottom: 10px;
		border-bottom: 1px solid #ebeef5;
		font-weight: 600;
	}

	.config-section {
		background: #fff;
		border-radius: 8px;
		padding: 20px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		margin-bottom: 20px;
	}

	.sub-title {
		font-size: 16px;
		color: #606266;
		margin: 20px 0 15px 0;
		font-weight: 500;
	}

	/* 自定义控件样式 */
	.custom-days-control,
	.custom-rooms-control {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.custom-days-control {
		gap: 10px;
	}

	.custom-rooms-control {
		width: 180px;
		margin: 8px 0;
		gap: 4px;
	}

	.control-btn {
		width: 40px;
		height: 40px;
		font-size: 18px;
		font-weight: bold;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 4px;
		padding: 0;
		min-width: auto;
		transition: all 0.2s ease;
	}

	.control-btn:disabled {
		background-color: #f5f7fa;
		border-color: #e4e7ed;
		color: #c0c4cc;
		cursor: not-allowed;
		opacity: 0.6;
	}

	.control-btn:not(:disabled):hover {
		background-color: #409eff;
		border-color: #409eff;
		color: white;
		transform: translateY(-1px);
		box-shadow: 0 2px 4px rgba(64, 158, 255, 0.1);
	}

	.control-btn:active:not(:disabled) {
		transform: translateY(0);
		box-shadow: none;
	}

	.custom-rooms-control .control-btn {
		width: 32px;
		height: 32px;
		font-size: 16px;
		border-radius: 8px;
		border: 1px solid #e4e7ed;
		background: #fff;
		color: #606266;
	}

	.days-input,
	.rooms-input {
		flex: 1;
		min-width: 0;
	}

	.days-input {
		width: 80px;
	}

	.rooms-input .el-input__wrapper {
		border-radius: 8px;
		box-shadow: 0 0 0 1px #e4e7ed inset;
		background: #fff;
		transition: all 0.2s ease;
		height: 32px;
		padding: 0 8px;
	}

	.rooms-input .el-input__wrapper:hover {
		box-shadow: 0 0 0 1px #c0c4cc inset;
	}

	.rooms-input .el-input__wrapper.is-focus {
		box-shadow: 0 0 0 1px #409eff inset;
		border-color: #409eff;
	}

	.days-input :deep(.el-input__inner),
	.rooms-input input {
		text-align: center;
		font-weight: 500;
		color: #303133;
		font-size: 14px;
		padding: 0 4px;
		height: 100%;
		border: none;
		outline: none;
		background: transparent;
		width: 100%;
	}

	.rooms-input input::-webkit-outer-spin-button,
	.rooms-input input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	.rooms-input input[type=number] {
		-moz-appearance: textfield;
	}

	.input-prepend-text {
		color: #606266;
		font-size: 12px;
		font-weight: 500;
		padding: 0 8px;
		border-right: 1px solid #e4e7ed;
		height: 18px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: #f5f7fa;
		border-radius: 4px 0 0 4px;
		margin-right: 8px;
	}

	/* 每日行程配置 */
	.daily-locations {
		margin-top: 20px;
	}

	.day-section {
		border: 1px solid #dcdfe6;
		border-radius: 8px;
		padding: 20px;
		margin-bottom: 20px;
		background: #fafafa;
		transition: all 0.3s;
		animation: fadeIn 0.5s ease;
	}

	.day-section:hover {
		border-color: #c0c4cc;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
	}

	.day-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
		padding-bottom: 10px;
		border-bottom: 1px solid #e4e7ed;
	}

	.day-title {
		font-size: 16px;
		font-weight: 600;
		color: #409eff;
		margin: 0;
	}

	.day-config {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.config-item {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;
		margin-bottom: 20px;
		width: 100%;
	}

	.config-label {
		width: 100%;
		text-align: left;
		font-weight: 500;
		color: #606266;
		font-size: 14px;
		margin-bottom: 8px;
	}

	/* 下拉选择器样式 */
	.location-select,
	.hotel-select,
	.car-select {
		width: 100%;
	}

	/* 选项样式 */
	.location-option,
	.hotel-option,
	.car-option {
		padding: 8px 0;
		line-height: 1.4;
	}

	.location-name,
	.hotel-name,
	.car-name {
		font-weight: 500;
		margin-bottom: 4px;
		color: #303133;
		font-size: 14px;
	}

	.location-address,
	.hotel-address,
	.car-details {
		font-size: 12px;
		color: #909399;
		margin-bottom: 2px;
	}

	.location-price,
	.hotel-price {
		font-size: 12px;
		color: #e6a23c;
		font-weight: 500;
	}

	.hotel-details {
		display: flex;
		justify-content: space-between;
		font-size: 12px;
		color: #909399;
		margin-bottom: 2px;
	}

	.hotel-description {
		font-size: 12px;
		color: #606266;
		margin-top: 4px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.occupied-badge {
		display: inline-block;
		padding: 2px 6px;
		background-color: #f56c6c;
		color: white;
		font-size: 12px;
		border-radius: 3px;
		margin-right: 8px;
		line-height: 1;
	}

	/* 已选预览样式 */
	.selected-preview {
		width: 100%;
		margin-top: 10px;
		border-radius: 6px;
		overflow: hidden;
		border: 1px solid #e4e7ed;
		background: white;
	}

	.preview-image {
		width: 100%;
		height: 120px;
		object-fit: cover;
	}

	.image-error {
		width: 100%;
		height: 120px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		background: #f5f7fa;
		color: #909399;
		font-size: 12px;
	}

	.preview-info {
		padding: 12px 15px;
		position: relative;
		overflow: hidden;
	}

	.preview-name {
		font-weight: 600;
		margin-bottom: 8px;
		color: #303133;
		font-size: 16px;
		display: flex;
		align-items: center;
		gap: 10px;
		line-height: 1.3;
		flex: 1;
		min-width: 0;
	}

	.preview-desc {
		font-size: 13px;
		color: #606266;
		line-height: 1.4;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.preview-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 8px;
		position: relative;
		z-index: 2;
	}

	.preview-price {
		font-size: 16px;
		font-weight: bold;
		color: #e6a23c;
	}

	.preview-address {
		font-size: 12px;
		color: #606266;
		display: flex;
		align-items: center;
		gap: 5px;
		margin: 5px 0;
	}

	.preview-address i {
		color: #909399;
	}

	/* 酒店预览特殊样式 */
	.selected-preview.hotel-preview {
		width: 100%;
		background: linear-gradient(135deg, #f8f9ff 0%, #f1f4ff 100%);
		border-radius: 12px;
		border: 1px solid #e0e7ff;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
		transition: all 0.3s ease;
		overflow: hidden;
		margin: 12px 0 16px 0;
		animation: fadeInUp 0.4s ease forwards;
	}

	.selected-preview.hotel-preview:hover {
		transform: translateY(-2px);
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
		border-color: #c7d2fe;
	}

	.selected-preview.hotel-preview .preview-info {
		padding: 20px;
	}

	.selected-preview.hotel-preview .preview-name {
		font-size: 18px;
		color: #1e293b;
	}

	.selected-preview.hotel-preview .preview-name i {
		color: #6366f1;
		font-size: 20px;
		background: rgba(99, 102, 241, 0.1);
		width: 36px;
		height: 36px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		transition: all 0.3s ease;
	}

	.selected-preview.hotel-preview:hover .preview-name i {
		background: rgba(99, 102, 241, 0.15);
		transform: scale(1.05);
	}

	.selected-preview.hotel-preview .preview-price {
		font-size: 15px;
		font-weight: 600;
		color: #10b981;
		background: rgba(16, 185, 129, 0.1);
		padding: 8px 16px;
		border-radius: 8px;
		white-space: nowrap;
		margin-left: 16px;
		display: flex;
		align-items: center;
		gap: 4px;
		flex-shrink: 0;
		transition: all 0.3s ease;
	}

	.selected-preview.hotel-preview:hover .preview-price {
		background: rgba(16, 185, 129, 0.15);
		transform: translateY(-1px);
	}

	.selected-preview.hotel-preview .preview-address {
		font-size: 14px;
		color: #64748b;
		margin-bottom: 12px;
		line-height: 1.5;
		padding: 8px 12px;
		background: rgba(255, 255, 255, 0.7);
		border-radius: 6px;
		border-left: 3px solid #94a3b8;
	}

	.selected-preview.hotel-preview .preview-address i {
		color: #94a3b8;
		font-size: 16px;
		flex-shrink: 0;
	}

	.selected-preview.hotel-preview .preview-desc {
		font-size: 13px;
		line-height: 1.6;
		color: #475569;
		background: white;
		padding: 16px;
		border-radius: 8px;
		border-left: 4px solid #6366f1;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
		transition: all 0.3s ease;
	}

	.selected-preview.hotel-preview:hover .preview-desc {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
	}

	.preview-info::before {
		content: '';
		position: absolute;
		top: 0;
		right: 0;
		width: 60px;
		height: 60px;
		background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
		border-radius: 0 0 0 40px;
		z-index: 1;
	}

	.preview-info::after {
		content: '';
		position: absolute;
		top: 10px;
		right: 10px;
		width: 20px;
		height: 20px;
		background-color: #6366f1;
		opacity: 0.1;
		border-radius: 50%;
		z-index: 1;
	}

	/* 餐饮安排样式 */
	.meal-config {
		width: 100%;
	}

	.meal-arrangement {
		width: 100%;
	}

	.daily-meal {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.meal-checkbox-group {
		display: flex;
		flex-wrap: wrap;
		gap: 20px;
	}

	.meal-price-info {
		font-size: 14px;
		color: #e6a23c;
		font-weight: 500;
		margin-top: 5px;
	}

	.el-form-item {
		margin-bottom: 20px;
	}

	.fee-input {
		width: 200px;
	}

	/* 费用和车辆配置 */
	.no-data {
		padding: 10px;
		text-align: center;
		color: #909399;
		font-size: 14px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	/* 操作按钮 */
	.action-buttons {
		margin-top: 30px;
		display: flex;
		gap: 15px;
		justify-content: center;
		padding-top: 20px;
		border-top: 1px solid #ebeef5;
	}

	/* 预览区域 */
	.preview-section {
		background: #fff;
		border-radius: 8px;
		padding: 20px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		margin-top: 20px;
	}

	.overview-cards {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
		gap: 20px;
		margin-bottom: 30px;
	}

	.overview-card {
		border-radius: 8px;
		overflow: hidden;
		transition: transform 0.3s, box-shadow 0.3s;
		cursor: pointer;
		animation: slideIn 0.5s ease;
		animation-fill-mode: both;
	}

	.overview-card:nth-child(1) {
		animation-delay: 0.1s;
	}

	.overview-card:nth-child(2) {
		animation-delay: 0.2s;
	}

	.overview-card:nth-child(3) {
		animation-delay: 0.3s;
	}

	.overview-card:nth-child(4) {
		animation-delay: 0.4s;
	}

	.overview-card:hover {
		transform: translateY(-5px);
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
	}

	.card-content {
		display: flex;
		align-items: center;
		padding: 20px;
	}

	.card-content i {
		font-size: 36px;
		margin-right: 15px;
		color: #409eff;
	}

	.card-title {
		font-size: 14px;
		color: #909399;
		margin-bottom: 5px;
	}

	.card-value {
		font-size: 20px;
		font-weight: bold;
		color: #303133;
	}

	.preview-tabs {
		border-radius: 8px;
		overflow: hidden;
		margin-top: 20px;
	}

	.itinerary-details {
		display: flex;
		flex-direction: column;
		gap: 30px;
		padding: 20px 0;
	}

	.day-itinerary {
		border: 1px solid #e4e7ed;
		border-radius: 8px;
		overflow: hidden;
		background: #fff;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	}

	.day-header {
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		color: white;
		padding: 20px;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.day-number {
		font-size: 24px;
		font-weight: bold;
	}

	.day-date {
		font-size: 16px;
		opacity: 0.9;
	}

	.itinerary-title {
		background: #f8f9fa;
		padding: 15px 20px;
		font-size: 18px;
		font-weight: 600;
		color: #333;
		border-bottom: 1px solid #e0e0e0;
	}

	.location-description {
		padding: 15px 20px;
		background: #f9fbfd;
		border-bottom: 1px solid #e0e0e0;
	}

	.description-line {
		margin-bottom: 5px;
		color: #666;
		font-size: 14px;
		line-height: 1.5;
	}

	.no-description {
		color: #999;
		font-style: italic;
		font-size: 14px;
	}

	.cost-breakdown {
		padding: 20px 0;
	}

	.meal-details {
		padding: 20px 0;
	}

	.cost-summary {
		margin-top: 20px;
		padding: 20px;
		background: #f8f9fa;
		border-radius: 8px;
		text-align: right;
	}

	.meal-summary {
		margin-top: 20px;
		padding: 20px;
		background: #f8f9fa;
		border-radius: 8px;
		text-align: right;
	}

	.total-cost {
		font-size: 18px;
		font-weight: bold;
		color: #333;
		margin-bottom: 10px;
	}

	.total-meal-cost {
		font-size: 18px;
		font-weight: bold;
		color: #333;
		margin-bottom: 10px;
	}

	.cost-note {
		color: #666;
		font-size: 14px;
		font-style: italic;
	}

	.meal-cost-note {
		color: #666;
		font-size: 14px;
		font-style: italic;
	}

	.transfer-footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 6px 12px;
		border-top: 1px solid #ebeef5;
		font-size: 12px;
	}

	.count-text {
		color: #909399;
	}

	/* 动画效果 */
	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(20px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@keyframes slideIn {
		from {
			opacity: 0;
			transform: translateX(-20px);
		}

		to {
			opacity: 1;
			transform: translateX(0);
		}
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(10px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@keyframes loading {
		0% {
			transform: translateX(-100%);
		}

		100% {
			transform: translateX(100%);
		}
	}

	/* 工具类 */
	.text-ellipsis {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.multi-line-ellipsis {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.flex-center {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.flex-between {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.flex-start {
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	.flex-end {
		display: flex;
		align-items: center;
		justify-content: flex-end;
	}

	/* Element Plus 组件样式覆盖 */
	:deep(.el-scrollbar__bar) {
		opacity: 0.6;
	}

	:deep(.el-scrollbar__thumb) {
		background-color: rgba(144, 147, 153, 0.3);
		transition: background-color 0.3s;
	}

	:deep(.el-scrollbar__thumb:hover) {
		background-color: rgba(144, 147, 153, 0.5);
	}

	:deep(.el-table) {
		border-radius: 8px;
		overflow: hidden;
	}

	:deep(.el-table__header) {
		background: #f5f7fa;
	}

	:deep(.el-table th) {
		background: #f5f7fa;
		color: #303133;
		font-weight: 600;
	}

	:deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
		background: #fafafa;
	}

	:deep(.el-table .cell) {
		padding: 12px 10px;
	}

	:deep(.el-tabs__nav-wrap::after) {
		height: 1px;
		background-color: #e4e7ed;
	}

	:deep(.el-tabs__item) {
		font-weight: 500;
		transition: all 0.3s;
	}

	:deep(.el-tabs__item.is-active) {
		color: #409eff;
		font-weight: 600;
	}

	:deep(.el-tabs__active-bar) {
		background-color: #409eff;
		height: 3px;
	}

	:deep(.el-select-dropdown__item) {
		padding: 8px 20px;
		transition: all 0.3s;
	}

	:deep(.el-select-dropdown__item:hover) {
		background-color: #f5f7fa;
	}

	:deep(.el-select-dropdown__item.is-selected) {
		background-color: #ecf5ff;
		color: #409eff;
	}

	:deep(.el-select-dropdown__item.is-disabled) {
		color: #c0c4cc;
		cursor: not-allowed;
		background-color: #fff;
	}

	:deep(.el-transfer) {
		display: flex;
		justify-content: center;
	}

	:deep(.el-transfer-panel) {
		width: 200px;
		height: 400px;
	}

	:deep(.el-transfer__buttons) {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 0 20px;
	}

	:deep(.el-transfer__button) {
		margin: 10px 0;
		width: 100%;
	}

	:deep(.el-loading-mask) {
		background-color: rgba(255, 255, 255, 0.9);
	}

	:deep(.el-loading-spinner .circular) {
		width: 42px;
		height: 42px;
	}

	:deep(.el-loading-text) {
		margin-top: 10px;
		color: #409eff;
		font-weight: 500;
	}

	:deep(.el-image-viewer__wrapper) {
		z-index: 9999;
	}

	:deep(.el-image-viewer__btn) {
		color: white;
		background-color: rgba(0, 0, 0, 0.5);
	}

	:deep(.el-image-viewer__btn:hover) {
		background-color: rgba(0, 0, 0, 0.8);
	}

	:deep(.el-message) {
		z-index: 9999;
		min-width: 200px;
	}

	:deep(.el-message--success) {
		background-color: #f0f9eb;
		border-color: #e1f3d8;
		color: #67c23a;
	}

	:deep(.el-message--warning) {
		background-color: #fdf6ec;
		border-color: #faecd8;
		color: #e6a23c;
	}

	:deep(.el-message--error) {
		background-color: #fef0f0;
		border-color: #fde2e2;
		color: #f56c6c;
	}

	:deep(.el-message--info) {
		background-color: #f4f4f5;
		border-color: #e9e9eb;
		color: #909399;
	}

	/* 响应式设计 */
	@media (max-width: 1200px) {
		.config-item {
			flex-direction: column;
			align-items: flex-start;
		}

		.config-label {
			text-align: left;
			margin-bottom: 8px;
		}

		.location-select,
		.hotel-select {
			width: 100%;
		}

		.selected-preview {
			max-width: 100%;
		}
	}

	@media (max-width: 768px) {
		.section-title {
			font-size: 18px;
		}

		.sub-title {
			font-size: 15px;
		}

		.day-title {
			font-size: 15px;
		}

		.config-label {
			font-size: 13px;
		}

		.overview-cards {
			grid-template-columns: 1fr;
		}

		.card-content {
			padding: 15px;
		}

		.day-header {
			flex-direction: column;
			align-items: flex-start;
			gap: 10px;
		}

		.day-number {
			font-size: 20px;
		}

		.day-date {
			font-size: 14px;
		}

		.meal-checkbox-group {
			flex-direction: column;
			gap: 10px;
		}

		.action-buttons {
			flex-direction: column;
		}

		.action-buttons .el-button {
			width: 100%;
		}

		.day-config {
			gap: 15px;
		}

		.config-item {
			margin-bottom: 15px;
		}

		.selected-preview {
			margin-top: 8px;
		}

		.preview-image {
			height: 100px;
		}

		.preview-info {
			padding: 10px;
		}

		/* 酒店预览响应式 */
		.selected-preview.hotel-preview .preview-header {
			flex-direction: column;
			align-items: flex-start;
			gap: 12px;
		}

		.selected-preview.hotel-preview .preview-price {
			margin-left: 0;
			align-self: flex-start;
		}

		.selected-preview.hotel-preview .preview-name {
			width: 100%;
		}

		.selected-preview.hotel-preview .preview-info {
			padding: 16px;
		}

		.selected-preview.hotel-preview {
			border-radius: 10px;
		}

		/* 自定义控件响应式 */
		.custom-rooms-control {
			width: 160px;
		}

		.custom-rooms-control .control-btn {
			width: 30px;
			height: 30px;
			font-size: 14px;
		}

		.rooms-input .el-input__wrapper {
			height: 30px;
		}

		.rooms-input input {
			font-size: 13px;
		}

		.input-prepend-text {
			font-size: 11px;
			padding: 0 6px;
		}
	}

	@media (max-width: 480px) {
		.custom-days-control {
			flex-direction: column;
			align-items: stretch;
		}

		.control-btn {
			width: 100%;
		}

		.days-input {
			width: 100%;
			margin: 10px 0;
		}

		.time-header {
			flex-direction: column;
			align-items: flex-start;
			gap: 8px;
		}

		.recommendation-header {
			flex-direction: column;
			align-items: flex-start;
			gap: 5px;
		}

		.meal-arrangement {
			flex-direction: column;
			align-items: flex-start;
		}

		.day-section {
			padding: 15px;
		}

		.config-label {
			font-size: 12px;
		}

		.location-select,
		.hotel-select {
			width: 100%;
		}

		.selected-preview {
			margin-top: 8px;
		}

		.preview-name {
			font-size: 14px;
		}

		.preview-desc {
			font-size: 12px;
		}

		/* 酒店预览响应式 */
		.selected-preview.hotel-preview .preview-name {
			font-size: 16px;
		}

		.selected-preview.hotel-preview .preview-name i {
			width: 32px;
			height: 32px;
			font-size: 18px;
		}

		.selected-preview.hotel-preview .preview-price {
			font-size: 14px;
			padding: 6px 12px;
		}

		.selected-preview.hotel-preview .preview-address {
			font-size: 13px;
		}

		.selected-preview.hotel-preview .preview-desc {
			font-size: 12px;
			padding: 12px;
		}

		/* 自定义控件响应式 */
		.custom-rooms-control {
			width: 140px;
		}

		.custom-rooms-control .control-btn {
			width: 28px;
			height: 28px;
		}

		.rooms-input .el-input__wrapper {
			height: 28px;
		}

		.input-prepend-text {
			display: none;
		}

		.rooms-input .el-input-group__prepend {
			padding: 0 4px;
		}
	}

	/* 打印样式 */
	@media print {

		.action-buttons,
		.el-form-item,
		.el-tabs__header,
		.el-tabs__nav-wrap {
			display: none !important;
		}

		.scheme-generator {
			gap: 0;
		}

		.config-section {
			display: none;
		}

		.preview-section {
			box-shadow: none;
			padding: 0;
		}

		.day-itinerary {
			break-inside: avoid;
			page-break-inside: avoid;
		}
	}
</style>